/* eslint-disable max-lines, max-lines-per-function, max-len */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import EditActionIcon from '../../../../Assets/images/svg/dar_edit.svg';
import DeleteActionIcon from '../../../../Assets/images/svg/dar_delete.svg';
import UseAxios from '../../../../Api/http/http';
import { useAuth } from '../../../../Hooks/useAuth';
import normalizeTableUtilityResponse from '../../../../Utils/tableUtilityResponse';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../../Api/resources/serviceURLs';
import { serviceName } from '../../../../Api/resources/serviceConstants';
import {
  bulkUpdateBudgetChangeRequestStatus,
  createBudgetChangeRequest,
  deleteBudgetChangeRequest,
  getBudgetChangeRequestById,
  getBudgetChangeRequestCostCategories,
  getBudgetChangeRequestResourceTypes,
  listBudgetChangeRequests,
  updateBudgetChangeRequest,
} from '../../../../Api/request/budgetChangeRequest';
import { masterProjectNameData } from '../../../../Api/request/projectOverviewMasterData';
import { MODULE_NAMES } from '../../../../Constants/modules';
import exportToExcel from '../../../../Utils/exportToExcel';
import BudgetChangeRequestModal from './BudgetChangeRequestModal';

const EXPORT_BASE_TITLE = 'Budget-change-request';

const BudgetChangeRequestSelectionContext = React.createContext({
  selectedRows: [],
  visibleSelectableRowIds: [],
});

const BULK_ACTION_OPTIONS = [
  { value: 'approve', label: 'Approved', status: 'Approved', color: '#28a745' },
  { value: 'reject', label: 'Rejected', status: 'Rejected', color: '#E30425' },
];

const COLUMN_WIDTHS = {
  selection: 40,
  action: 76,
};

const NUMERIC_COLUMN_FIELDS = new Set([
  'approverID',
  'requestedByID',
  'totalEffortHrs',
  'totalCostInr',
]);

const TRUNCATED_TEXT_FIELDS = new Set([
  'reasonForVersionChange',
  'approvalRejectComment',
]);

const EFFORT_COLUMN_FIELDS = new Set([
  'totalEffortHrs',
  'effortBa',
  'effortArchitect',
  'effortBeDeveloper',
  'effortFeDeveloper',
  'effortEtlDeveloper',
]);

const COST_COLUMN_FIELDS = new Set([
  'totalCostInr',
  'costOthers',
]);

const GLOBAL_FILTER_FIELDS = new Set([
  'projectName',
  'requestStatus',
  'requestedDate',
  'requestedByName',
  'approverName',
]);

const normalizeStatusLabel = (value) => String(value || '')
  .trim()
  .toLowerCase();

const hasPermissionAction = (permissions = {}, moduleName, action) => (
  Array.isArray(permissions?.[moduleName]) && permissions[moduleName].includes(action)
);

const hasAnyPermissionAction = (permissions = {}, moduleName) => (
  Array.isArray(permissions?.[moduleName]) && permissions[moduleName].length > 0
);

const isDraftStatus = (value) => normalizeStatusLabel(value) === 'draft';
const isOpenStatus = (value) => normalizeStatusLabel(value) === 'open';
const isSelectableStatus = (value) => isDraftStatus(value) || isOpenStatus(value);
const isEditableStatus = (value) => isDraftStatus(value);
const isDeletableStatus = (value) => isDraftStatus(value) || isOpenStatus(value);
const isInactiveRequest = (row = {}) => String(row?.isActive || '')
  .trim()
  .toUpperCase() === 'N';

const getEffectiveStatusLabel = (row = {}) => (
  isInactiveRequest(row)
    ? 'Cancelled'
    : sanitizeText(row?.requestStatus, '-')
);

const getEffectiveStatusColor = (row = {}) => (
  isInactiveRequest(row)
    ? '#E8A700'
    : sanitizeText(row?.requestStatusColor, '')
);

const isNumericColumnField = (field = '') => (
  NUMERIC_COLUMN_FIELDS.has(field)
  || String(field).startsWith('effort')
  || String(field).startsWith('cost')
);

const COST_ANALYSIS_REPORT_ROWS = [
  {
    category: 'Travel Cost',
    planned: '5,60,000',
    actual: '5,60,000',
    actualDifference: '0',
    actualDifferenceTone: 'neutral',
    reEstimated: '7,00,000',
    reEstimatedDifference: '-1,40,000',
    reEstimatedDifferenceTone: 'negative',
  },
  {
    category: 'Accommodation cost',
    planned: '1,45,000',
    actual: '1,45,000',
    actualDifference: '1,00,000',
    actualDifferenceTone: 'positive',
    reEstimated: '3,00,000',
    reEstimatedDifference: '-1,55,000',
    reEstimatedDifferenceTone: 'negative',
  },
  {
    category: 'Hardware and software cost',
    planned: '1,50,000',
    actual: '2,00,000',
    actualDifference: '-50,000',
    actualDifferenceTone: 'negative',
    reEstimated: '3,00,000',
    reEstimatedDifference: '-1,50,000',
    reEstimatedDifferenceTone: 'negative',
  },
  {
    category: 'Consultant cost',
    planned: '2,00,000',
    actual: '1,90,000',
    actualDifference: '10,000',
    actualDifferenceTone: 'positive',
    reEstimated: '2,00,000',
    reEstimatedDifference: '0',
    reEstimatedDifferenceTone: 'neutral',
  },
  {
    category: 'Other cost',
    planned: '3,00,000',
    actual: '4,00,000',
    actualDifference: '-1,00,000',
    actualDifferenceTone: 'negative',
    reEstimated: '6,00,000',
    reEstimatedDifference: '-3,00,000',
    reEstimatedDifferenceTone: 'negative',
  },
];

const COST_ANALYSIS_REPORT_TOTAL = {
  planned: '13,65,000',
  actual: '13,95,000',
  actualDifference: '-40,000',
  actualDifferenceTone: 'negative',
  reEstimated: '21,10,000',
  reEstimatedDifference: '-7,45,000',
  reEstimatedDifferenceTone: 'negative',
};

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api/v1/budget-change-requests`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api/v1/budget-change-requests`;
};

const baseURL = fetchBaseURL();

const MONTH_ABBREVIATION_TO_NUMBER = {
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  may: '05',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  oct: '10',
  nov: '11',
  dec: '12',
};

const formatDisplayDate = (value) => {
  if (!value || value === '-') return '-';

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return String(value);

  return parsedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).replace(/ /g, '-');
};

const hexToRgba = (hex, alpha) => {
  const normalizedHex = String(hex || '').trim()
    .replace('#', '');
  const validHex = normalizedHex.length === 3
    ? normalizedHex.split('').map((char) => `${char}${char}`)
      .join('')
    : normalizedHex;

  if (!/^[0-9a-fA-F]{6}$/.test(validHex)) {
    return '';
  }

  const red = Number.parseInt(validHex.slice(0, 2), 16);
  const green = Number.parseInt(validHex.slice(2, 4), 16);
  const blue = Number.parseInt(validHex.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const sanitizeText = (value, fallback = '-') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const formatFilterDateValue = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '';
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildDefaultNinetyDayDateRange = () => {
  const toDate = new Date();
  const fromDate = new Date(toDate);
  fromDate.setDate(fromDate.getDate() - 89);

  return {
    from: formatFilterDateValue(fromDate),
    to: formatFilterDateValue(toDate),
  };
};

const normalizeFilterValues = (values = []) => (
  (Array.isArray(values) ? values : [values])
    .map((item) => {
      if (item && typeof item === 'object') {
        return String(item?.value ?? item?.label ?? item?.name ?? '').trim();
      }

      return String(item ?? '').trim();
    })
    .filter(Boolean)
);

const toFilterOptionObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const resolvedValue = String(
      value?.value
      ?? value?.id
      ?? value?.name
      ?? value?.label
      ?? '',
    ).trim();

    if (!resolvedValue) {
      return null;
    }

    const resolvedLabel = String(
      value?.label
      ?? value?.name
      ?? value?.value
      ?? value?.id
      ?? resolvedValue,
    ).trim() || resolvedValue;

    return {
      ...value,
      id: value?.id ?? resolvedValue,
      value: resolvedValue,
      label: resolvedLabel,
      name: value?.name ?? resolvedLabel,
    };
  }

  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) {
    return null;
  }

  return {
    id: normalizedValue,
    value: normalizedValue,
    label: normalizedValue,
    name: normalizedValue,
  };
};

const toFilterOptionArray = (values = []) => normalizeFilterValues(values)
  .map((value) => toFilterOptionObject(value))
  .filter(Boolean);

const getProjectNameMasterRows = (response = {}) => (
  response?.data?.data?.rows
  || response?.data?.rows
  || response?.data?.data?.masterProjectNameDetails
  || response?.data?.masterProjectNameDetails
  || response?.data?.data?.masterProjectNames
  || response?.data?.masterProjectNames
  || response?.data?.data?.projectNames
  || response?.data?.projectNames
  || []
);

const normalizeProjectNameMasterOptions = (rows = []) => (
  (Array.isArray(rows) ? rows : [])
    .map((item) => {
      const name = sanitizeText(
        item?.masterProjectName
        ?? item?.master_project_name
        ?? item?.projectName
        ?? item?.project_name
        ?? item?.name
        ?? item?.label
        ?? item?.project?.projectName
        ?? item?.project?.masterProjectName,
        '',
      );
      const projectIdentifier = toProjectId(
        item?.masterProjectId
        ?? item?.master_project_id
        ?? item?.projectId
        ?? item?.project_id
        ?? item?.id
        ?? item?.value
        ?? item?.project?.projectId
        ?? item?.project?.project_id
        ?? item?.project?.projectSeqId
        ?? item?.project?.project_seq_id,
      );

      if (!name || !projectIdentifier) {
        return null;
      }

      return {
        id: projectIdentifier,
        projectId: projectIdentifier,
        value: name,
        label: name,
        name,
      };
    })
    .filter(Boolean)
);

const getFilterItem = (tableConfiguration = [], fields = []) => {
  const targetFields = Array.isArray(fields) ? fields : [fields];

  for (const column of Array.isArray(tableConfiguration) ? tableConfiguration : []) {
    for (const menuGroup of Array.isArray(column?.menuItems) ? column.menuItems : []) {
      for (const item of Array.isArray(menuGroup?.items) ? menuGroup.items : []) {
        if (item?.type === 'filter' && targetFields.includes(item?.field)) {
          return item;
        }
      }
    }
  }

  return null;
};

const arePayloadsEqual = (left = {}, right = {}) => JSON.stringify(left) === JSON.stringify(right);

const getNormalizedFilterOption = (value) => toFilterOptionObject(value);

const getNormalizedFilterOptions = (values = []) => toFilterOptionArray(values);

const toProjectId = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

const getProjectContextFromRawProject = (project = {}) => ({
  projectId: toProjectId(
    project?.projectId
    ?? project?.project_id
    ?? project?.id,
  ),
  projectName: sanitizeText(
    project?.projectName
    ?? project?.project_name
    ?? project?.name,
    '',
  ),
});

const getProjectContextFromSelection = (
  selectedValue,
  options = [],
  fallbackProjectId = null,
  fallbackProjectName = '',
  responseProject = {},
) => {
  const normalizedSelection = getNormalizedFilterOption(selectedValue);
  const normalizedOptions = Array.isArray(options) ? options : [];
  const selectedProjectName = sanitizeText(
    normalizedSelection?.label
    ?? normalizedSelection?.name
    ?? normalizedSelection?.value,
    '',
  );
  const matchedOption = normalizedOptions.find((option) => {
    const normalizedOption = getNormalizedFilterOption(option);
    const optionProjectName = sanitizeText(
      normalizedOption?.label
      ?? normalizedOption?.name
      ?? normalizedOption?.value,
      '',
    );
    return selectedProjectName
      && optionProjectName.toLowerCase() === selectedProjectName.toLowerCase();
  });
  const normalizedMatchedOption = getNormalizedFilterOption(matchedOption);
  const responseProjectId = toProjectId(
    responseProject?.projectId
    ?? responseProject?.project_id
    ?? responseProject?.id,
  );
  const responseProjectName = sanitizeText(
    responseProject?.projectName
    ?? responseProject?.project_name
    ?? responseProject?.name,
    '',
  );

  return {
    projectId: toProjectId(
      normalizedSelection?.id
      ?? normalizedSelection?.projectId
      ?? normalizedSelection?.project_id
      ?? normalizedMatchedOption?.id
      ?? normalizedMatchedOption?.projectId
      ?? normalizedMatchedOption?.project_id
      ?? normalizedSelection?.value,
    ) || responseProjectId || toProjectId(fallbackProjectId),
    projectName: sanitizeText(
      normalizedSelection?.label
      ?? normalizedSelection?.name
      ?? normalizedSelection?.value,
      '',
    ) || sanitizeText(
      normalizedMatchedOption?.label
      ?? normalizedMatchedOption?.name
      ?? normalizedMatchedOption?.value,
      '',
    ) || responseProjectName || sanitizeText(fallbackProjectName, ''),
  };
};

const getProjectContextFromRows = (
  rows = [],
  selectedProjectName = '',
  fallbackProjectId = null,
  fallbackProjectName = '',
) => {
  const normalizedSelectedProjectName = sanitizeText(selectedProjectName, '').toLowerCase();
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const matchingRows = normalizedSelectedProjectName
    ? normalizedRows.filter((row) => (
      sanitizeText(
        row?.projectName
        ?? row?.project_name
        ?? row?.projectDetail?.projectName
        ?? row?.projectDetail?.project_name,
        '',
      ).toLowerCase() === normalizedSelectedProjectName
    ))
    : normalizedRows;

  const uniqueProjectContexts = matchingRows.reduce((accumulator, row) => {
    const rowProjectId = toProjectId(
      row?.projectId
      ?? row?.project_id
      ?? row?.projectDetail?.projectId
      ?? row?.projectDetail?.project_id
      ?? row?.project_seq_id
      ?? row?.projectSeqId,
    );
    if (!rowProjectId) {
      return accumulator;
    }

    const contextKey = String(rowProjectId);
    if (accumulator.some((candidate) => candidate.key === contextKey)) {
      return accumulator;
    }

    return [
      ...accumulator,
      {
        key: contextKey,
        projectId: rowProjectId,
        projectName: sanitizeText(
          row?.projectName
          ?? row?.project_name
          ?? row?.projectDetail?.projectName
          ?? row?.projectDetail?.project_name,
          fallbackProjectName,
        ),
      },
    ];
  }, []);

  if (uniqueProjectContexts.length === 1) {
    const [resolvedContext] = uniqueProjectContexts;
    return {
      projectId: resolvedContext.projectId,
      projectName: resolvedContext.projectName,
    };
  }

  return {
    projectId: toProjectId(fallbackProjectId),
    projectName: sanitizeText(fallbackProjectName, ''),
  };
};

const getProjectSelectionName = (selectedValue) => {
  const normalizedSelection = getNormalizedFilterOption(selectedValue);
  return sanitizeText(
    normalizedSelection?.label
    ?? normalizedSelection?.name
    ?? normalizedSelection?.value,
    '',
  );
};

const isSameProjectSelectionName = (firstValue, secondValue) => (
  getProjectSelectionName(firstValue).toLowerCase()
  === getProjectSelectionName(secondValue).toLowerCase()
);

const getSingleSelectFilterValue = (filterItem, fallbackValue = null) => (
  getNormalizedFilterOption(
    filterItem?.selectedValue
    ?? filterItem?.selectedValues?.[0]
    ?? fallbackValue,
  )
);

const getMultiSelectFilterValues = (filterItem, fallbackValues = []) => (
  getNormalizedFilterOptions(
    Array.isArray(filterItem?.selectedValues) && filterItem.selectedValues.length > 0
      ? filterItem.selectedValues
      : fallbackValues,
  )
);

const buildTextFilterPayload = (filterItem, fallbackValue = '') => ({
  inputValue: String(filterItem?.inputValue ?? fallbackValue ?? '').trim(),
});

const buildSingleSelectFilterPayload = (filterItem, fallbackValue = null) => {
  const selectedValue = getSingleSelectFilterValue(filterItem, fallbackValue);

  return {
    selectedValue,
    selectedValues: selectedValue ? [selectedValue] : [],
  };
};

const buildMultiSelectFilterPayload = (filterItem, fallbackValues = []) => ({
  selectedValues: getMultiSelectFilterValues(filterItem, fallbackValues),
});

const normalizeDateRangeArray = (range = []) => {
  const [fromValue, toValue] = Array.isArray(range) ? range : [null, null];
  const from = String(fromValue || '').trim();
  const to = String(toValue || '').trim();

  return [from || null, to || null];
};

const getDateRangeArrayFromPayload = (payloadItem = {}) => normalizeDateRangeArray(
  Array.isArray(payloadItem?.range)
    ? payloadItem.range
    : payloadItem?.selectedValues,
);

const hasCompleteDateRange = (range = []) => {
  const [fromValue, toValue] = normalizeDateRangeArray(range);
  return Boolean(fromValue && toValue);
};

const buildDateRangeFilterPayload = (filterItem, fallbackRange = [null, null]) => {
  const currentRange = normalizeDateRangeArray(filterItem?.range);
  const effectiveRange = hasCompleteDateRange(currentRange) ? currentRange : normalizeDateRangeArray(fallbackRange);

  return {
    selectedValues: hasCompleteDateRange(effectiveRange) ? [...effectiveRange] : [],
    range: [...effectiveRange],
  };
};

const buildRequestFilterArray = (filterPayload = {}) => {
  const requestFilters = [];
  const textFilterFields = [
    'eern',
    'version',
    'reasonForVersionChange',
    'approverID',
    'approvalRejectComment',
    'requestedByID',
  ];
  const singleSelectFields = ['projectName', 'approverName', 'requestedByName'];
  const multiSelectFields = ['requestStatus'];
  const dateRangeFields = ['requestedDate'];

  textFilterFields.forEach((field) => {
    const inputValue = String(filterPayload?.[field]?.inputValue || '').trim();
    if (!inputValue) return;

    requestFilters.push({
      field,
      inputValue,
      isGlobalFilter: true,
    });
  });

  singleSelectFields.forEach((field) => {
    const selectedValue = getSingleSelectFilterValue(filterPayload?.[field]);
    if (!selectedValue) return;

    requestFilters.push({
      field,
      selectedValue,
      isGlobalFilter: true,
    });
  });

  multiSelectFields.forEach((field) => {
    const selectedValues = getMultiSelectFilterValues(filterPayload?.[field]);
    if (!selectedValues.length) return;

    requestFilters.push({
      field,
      selectedValues,
      isGlobalFilter: true,
    });
  });

  dateRangeFields.forEach((field) => {
    const range = getDateRangeArrayFromPayload(filterPayload?.[field]);
    if (!hasCompleteDateRange(range)) return;

    requestFilters.push({
      field,
      selectedValues: [...range],
      range: [...range],
      isGlobalFilter: true,
    });
  });

  return requestFilters;
};

const DATE_RANGE_INPUT_SELECTORS = [
  '.tu-value-condition-filter input',
  '.calendar-filter-container input',
  '.tu-filter-panel-template input',
  '.tu-filter-detail-template input',
];

const normalizeDateInputValue = (value) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return null;

  const isoMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return normalizedValue;

  const slashYearFirstMatch = normalizedValue.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (slashYearFirstMatch) {
    const [, year, month, day] = slashYearFirstMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const slashDayFirstMatch = normalizedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashDayFirstMatch) {
    const [, day, month, year] = slashDayFirstMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const displayMatch = normalizedValue.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  if (displayMatch) {
    const [, day, month, year] = displayMatch;
    const monthNumber = MONTH_ABBREVIATION_TO_NUMBER[month.toLowerCase()];
    return monthNumber ? `${year}-${monthNumber}-${day.padStart(2, '0')}` : null;
  }

  const parsedDate = new Date(normalizedValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return formatFilterDateValue(parsedDate) || null;
  }

  return null;
};

const getDateRangeInputs = (rootNode) => {
  if (!rootNode?.querySelectorAll) {
    return [];
  }

  const inputElements = new Set();
  DATE_RANGE_INPUT_SELECTORS.forEach((selector) => {
    rootNode.querySelectorAll(selector).forEach((inputElement) => {
      inputElements.add(inputElement);
    });
  });

  return [...inputElements];
};

const getDateRangeValuesFromRoot = (rootNode) => {
  const visibleDateValues = getDateRangeInputs(rootNode)
    .filter((inputElement) => inputElement?.offsetParent !== null)
    .map((inputElement) => normalizeDateInputValue(inputElement?.value))
    .filter(Boolean);

  if (visibleDateValues.length < 2) {
    return [null, null];
  }

  return visibleDateValues.slice(0, 2);
};

const getVisibleDateRangePanels = (rootNode = document) => {
  if (!rootNode?.querySelectorAll) {
    return [];
  }

  const panelElements = new Set();
  [
    '.tu-value-condition-filter',
    '.calendar-filter-container',
    '.tu-filter-panel-template',
    '.tu-filter-detail-template',
  ].forEach((selector) => {
    rootNode.querySelectorAll(selector).forEach((element) => {
      if (element?.offsetParent !== null) {
        panelElements.add(element);
      }
    });
  });

  return [...panelElements];
};

const getVisibleMultiSelectValues = (rootNode) => (
  [
    ...Array.from(rootNode?.querySelectorAll?.(
      [
        '.tu-multi-select-option.selected .tu-multi-select-option-label',
        '.tu-multi-select-option.is-selected .tu-multi-select-option-label',
        '.tu-multi-select-option[aria-selected="true"] .tu-multi-select-option-label',
        '.p-highlight .tu-multi-select-option-label',
      ].join(','),
    ) || []),
    ...Array.from(rootNode?.querySelectorAll?.('input[type="checkbox"]:checked') || [])
      .map((inputElement) => (
        inputElement.closest?.('.tu-multi-select-option')
        || inputElement.closest?.('label')
        || inputElement.parentElement
      ))
      .filter(Boolean),
  ]
    .map((node) => String(node?.textContent || '').trim())
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
);

const getVisibleMultiSelectPanels = (rootNode = document) => {
  if (!rootNode?.querySelectorAll) {
    return [];
  }

  return Array.from(rootNode.querySelectorAll('.tu-multi-select-filter'))
    .filter((panelElement) => panelElement?.offsetParent !== null);
};

const getVisibleSingleSelectValue = (rootNode) => String(
  rootNode?.querySelector?.('.tu-single-select-input')?.value
  || rootNode?.querySelector?.('.tu-single-select-value')?.textContent
  || rootNode?.querySelector?.('.tu-single-select-selected-value')?.textContent
  || rootNode?.querySelector?.('.p-dropdown-label:not(.p-placeholder)')?.textContent
  || rootNode?.querySelector?.('[role="combobox"]')?.textContent
  || '',
).trim();

const getVisibleSingleSelectPanels = (rootNode = document) => {
  if (!rootNode?.querySelectorAll) {
    return [];
  }

  return Array.from(rootNode.querySelectorAll('.tu-single-select-filter'))
    .filter((panelElement) => panelElement?.offsetParent !== null);
};

const getNormalizedFilterPanelTitle = (rootNode) => String(
  rootNode?.querySelector?.('.tu-single-select-title')?.textContent
  || rootNode?.querySelector?.('.tu-multi-select-title')?.textContent
  || rootNode?.querySelector?.('.tu-filter-section-title')?.textContent
  || rootNode?.querySelector?.('.tu-filter-panel-title')?.textContent
  || rootNode?.querySelector?.('.tu-value-condition-title')?.textContent
  || '',
)
  .trim()
  .toLowerCase();

const getActiveFilterLabel = (rootNode = document) => String(
  rootNode?.querySelector?.('.tu-filter-subitem.active .tu-filter-subtext')?.textContent
  || rootNode?.querySelector?.('.tu-filter-column-item.active .tu-filter-column-text')?.textContent
  || rootNode?.querySelector?.('.tu-filter-subitem.active')?.textContent
  || rootNode?.querySelector?.('.tu-filter-column-item.active')?.textContent
  || '',
)
  .trim()
  .toLowerCase();

const getSingleSelectFieldFromLabel = (label = '') => {
  if (label.includes('project name')) {
    return 'projectName';
  }

  if (label.includes('approver name')) {
    return 'approverName';
  }

  if (label.includes('requested by')) {
    return 'requestedByName';
  }

  return '';
};

const resolveSingleSelectFieldName = (rootNode, fallbackRootNode = document) => (
  getSingleSelectFieldFromLabel(getNormalizedFilterPanelTitle(rootNode))
  || getSingleSelectFieldFromLabel(getActiveFilterLabel(fallbackRootNode))
);

const getMultiSelectFieldFromLabel = (label = '') => {
  if (label.includes('request status')) {
    return 'requestStatus';
  }

  return '';
};

const resolveMultiSelectFieldName = (rootNode, fallbackRootNode = document) => (
  getMultiSelectFieldFromLabel(getNormalizedFilterPanelTitle(rootNode))
  || getMultiSelectFieldFromLabel(getActiveFilterLabel(fallbackRootNode))
);

const getDateRangeFieldFromLabel = (label = '') => {
  if (label.includes('requested date')) {
    return 'requestedDate';
  }

  return '';
};

const resolveDateRangeFieldName = (rootNode, fallbackRootNode = document) => (
  getDateRangeFieldFromLabel(getNormalizedFilterPanelTitle(rootNode))
  || getDateRangeFieldFromLabel(getActiveFilterLabel(fallbackRootNode))
);

const buildDefaultAdditionalPayload = ({
  projectId,
  projectName,
}) => {
  const defaultDateRange = buildDefaultNinetyDayDateRange();
  const defaultProjectName = getNormalizedFilterOption(projectName);

  return {
    project_id: Number(projectId),
    eern: { inputValue: '' },
    version: { inputValue: '' },
    reasonForVersionChange: { inputValue: '' },
    projectName: {
      selectedValue: defaultProjectName,
      selectedValues: defaultProjectName ? [defaultProjectName] : [],
    },
    requestStatus: {
      selectedValues: [],
    },
    approverID: { inputValue: '' },
    approverName: {
      selectedValue: null,
      selectedValues: [],
    },
    approvalRejectComment: { inputValue: '' },
    requestedByID: { inputValue: '' },
    requestedByName: {
      selectedValue: null,
      selectedValues: [],
    },
    requestedDate: {
      selectedValues: [defaultDateRange.from, defaultDateRange.to],
      range: [defaultDateRange.from, defaultDateRange.to],
    },
  };
};

const getAppliedAdditionalPayload = (tableConfiguration = [], basePayload = {}) => {
  const nextPayload = {
    ...(basePayload?.project_id ? { project_id: basePayload.project_id } : {}),
  };
  const projectNameFilter = getFilterItem(tableConfiguration, 'projectName');
  const eernFilter = getFilterItem(tableConfiguration, 'eern');
  const versionFilter = getFilterItem(tableConfiguration, 'version');
  const reasonForVersionChangeFilter = getFilterItem(tableConfiguration, 'reasonForVersionChange');
  const requestStatusFilter = getFilterItem(tableConfiguration, 'requestStatus');
  const approverIdFilter = getFilterItem(tableConfiguration, 'approverID');
  const approverNameFilter = getFilterItem(tableConfiguration, 'approverName');
  const requestedDateFilter = getFilterItem(tableConfiguration, 'requestedDate');
  const approvalRejectCommentFilter = getFilterItem(tableConfiguration, 'approvalRejectComment');
  const requestedByIdFilter = getFilterItem(tableConfiguration, 'requestedByID');
  const requestedByNameFilter = getFilterItem(tableConfiguration, 'requestedByName');

  nextPayload.eern = buildTextFilterPayload(eernFilter, basePayload?.eern?.inputValue);
  nextPayload.version = buildTextFilterPayload(versionFilter, basePayload?.version?.inputValue);
  nextPayload.reasonForVersionChange = buildTextFilterPayload(
    reasonForVersionChangeFilter,
    basePayload?.reasonForVersionChange?.inputValue,
  );
  nextPayload.projectName = buildSingleSelectFilterPayload(
    projectNameFilter,
    basePayload?.projectName?.selectedValue,
  );
  nextPayload.requestStatus = buildMultiSelectFilterPayload(
    requestStatusFilter,
    basePayload?.requestStatus?.selectedValues,
  );
  nextPayload.approverID = buildTextFilterPayload(approverIdFilter, basePayload?.approverID?.inputValue);
  nextPayload.approverName = buildSingleSelectFilterPayload(
    approverNameFilter,
    basePayload?.approverName?.selectedValue,
  );
  nextPayload.approvalRejectComment = buildTextFilterPayload(
    approvalRejectCommentFilter,
    basePayload?.approvalRejectComment?.inputValue,
  );
  nextPayload.requestedByID = buildTextFilterPayload(
    requestedByIdFilter,
    basePayload?.requestedByID?.inputValue,
  );
  nextPayload.requestedByName = buildSingleSelectFilterPayload(
    requestedByNameFilter,
    basePayload?.requestedByName?.selectedValue,
  );
  nextPayload.requestedDate = buildDateRangeFilterPayload(
    requestedDateFilter,
    getDateRangeArrayFromPayload(basePayload?.requestedDate),
  );
  nextPayload.filter = buildRequestFilterArray(nextPayload);

  return nextPayload;
};

const normalizeResourceSkillOptions = (rows = []) => (
  Array.isArray(rows)
    ? rows.map((row) => ({
      value: row?.value ?? row?.id,
      label: row?.label ?? row?.name ?? '',
    })).filter((row) => row.value !== undefined && row.value !== null && row.label)
    : []
);

const normalizeCostCategoryOptions = (rows = []) => (
  Array.isArray(rows)
    ? rows.map((row) => ({
      value: row?.value ?? row?.id,
      label: row?.label ?? row?.category ?? '',
    })).filter((row) => row.value !== undefined && row.value !== null && row.label)
    : []
);

const normalizeDetailPayload = (payload = {}, fallbackReason = '') => ({
  header: payload?.header || {},
  effortRows: Array.isArray(payload?.effortRows)
    ? payload.effortRows.map((row) => ({
      resourceId: row?.resource_id ?? row?.resourceId,
      resourceSkill: row?.resource_name ?? row?.resourceSkill ?? '',
      effort: row?.efforts ?? row?.effort ?? '',
      comment: row?.comments ?? row?.comment ?? '',
    }))
    : [],
  costRows: Array.isArray(payload?.costRows)
    ? payload.costRows.map((row) => ({
      costCategoryId: row?.cost_category_id ?? row?.costCategoryId,
      costCategory: row?.cost_category_name ?? row?.costCategory ?? '',
      amount: row?.cost ?? row?.amount ?? '',
      comment: row?.comments ?? row?.comment ?? '',
    }))
    : [],
  reasonForVersionChange:
    fallbackReason
    || payload?.header?.reasonForVersionChange
    || '',
});

const mapModalPayloadToRequest = ({ payload, isDraft, projectId }) => ({
  project_id: Number(projectId),
  reason: payload?.reasonForVersionChange || '',
  mode: isDraft ? 'draft' : 'open',
  effort_rows: Array.isArray(payload?.effortRows)
    ? payload.effortRows.map((row) => ({
      resource_id: Number.parseInt(String(row?.resourceId || ''), 10),
      efforts: Number.parseFloat(String(row?.effort || '').replace(/,/g, '')),
      comments: row?.comment || '',
    })).filter((row) => Number.isFinite(row.resource_id) && Number.isFinite(row.efforts))
    : [],
  cost_rows: Array.isArray(payload?.costRows)
    ? payload.costRows.map((row) => ({
      cost_category_id: Number.parseInt(String(row?.costCategoryId || ''), 10),
      cost: Number.parseFloat(String(row?.amount || '').replace(/,/g, '')),
      comments: row?.comment || '',
    })).filter((row) => Number.isFinite(row.cost_category_id) && Number.isFinite(row.cost))
    : [],
});

const getApiMessage = (response, fallback) => {
  const resolvedMessage = response?.data?.message
    || response?.data?.data?.message
    || response?.response?.data?.message
    || response?.response?.data?.data?.message
    || response?.data?.response?.data?.message
    || '';

  if (resolvedMessage && !/request failed with status code/i.test(String(resolvedMessage))) {
    return resolvedMessage;
  }

  return fallback;
};

const sanitizeExportSegment = (value, fallback = 'Project') => {
  const text = String(value || '')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return text || fallback;
};

const formatExportDateTime = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');

  return {
    date: `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`,
    time: `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`,
  };
};

const resolveBcrProjectName = (projectName, exportContext = {}) => {
  const responseProjectName = String(
    exportContext?.response?.data?.project?.projectName
    || exportContext?.response?.data?.data?.project?.projectName
    || exportContext?.response?.data?.projectName
    || exportContext?.response?.data?.data?.projectName
    || '',
  ).trim();

  if (responseProjectName) {
    return responseProjectName;
  }

  const tableRows = Array.isArray(exportContext?.data?.tableData)
    ? exportContext.data.tableData
    : Array.isArray(exportContext?.data)
      ? exportContext.data
      : [];

  const rowProjectName = String(
    tableRows.find((row) => String(row?.projectName || '').trim())?.projectName
    || '',
  ).trim();

  if (rowProjectName) {
    return rowProjectName;
  }

  return String(projectName || '').trim();
};

const buildBudgetChangeRequestExportName = (projectName, exportContext = {}) => {
  const normalizedProjectName = sanitizeExportSegment(
    resolveBcrProjectName(projectName, exportContext),
  );
  const { date, time } = formatExportDateTime();

  return `${EXPORT_BASE_TITLE} - ${normalizedProjectName} - ${date} - ${time}`;
};

const buildBudgetChangeRequestSheetName = (projectName, exportContext = {}) => {
  const normalizedProjectName = sanitizeExportSegment(
    resolveBcrProjectName(projectName, exportContext),
  )
    .replace(/[[\]]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const { date, time } = formatExportDateTime();
  const fullName = `${EXPORT_BASE_TITLE} - ${normalizedProjectName} - ${date} - ${time}`;

  return fullName.slice(0, 31);
};

const getBcrExportColumns = ({ canViewEffort, canViewCost }) => ([
  { header: 'BCRN', key: 'eern', width: 16 },
  { header: 'Version', key: 'version', width: 14 },
  { header: 'Reason For Version Change', key: 'reasonForVersionChange', width: 34 },
  { header: 'Project Name', key: 'projectName', width: 26 },
  { header: 'Project Vertical', key: 'projectVertical', width: 20 },
  { header: 'Request Status', key: 'requestStatus', width: 18 },
  { header: 'Approver ID', key: 'approverID', width: 16 },
  { header: 'Approver Name', key: 'approverName', width: 24 },
  { header: 'Approval Date', key: 'approvalDate', width: 18 },
  { header: 'Approval/Reject Comment', key: 'approvalRejectComment', width: 34 },
  ...(canViewEffort
    ? [
      { header: 'Total Effort (Hrs)', key: 'totalEffortHrs', width: 18 },
      { header: 'BA', key: 'effortBa', width: 14 },
      { header: 'Architect', key: 'effortArchitect', width: 16 },
      { header: 'BE Developer', key: 'effortBeDeveloper', width: 18 },
      { header: 'FE Developer', key: 'effortFeDeveloper', width: 18 },
      { header: 'ETL Developer', key: 'effortEtlDeveloper', width: 18 },
    ]
    : []),
  ...(canViewCost
    ? [
      { header: 'Total Cost (INR)', key: 'totalCostInr', width: 22 },
      { header: 'Others', key: 'costOthers', width: 16 },
    ]
    : []),
  { header: 'Requested By ID', key: 'requestedByID', width: 18 },
  { header: 'Requested By', key: 'requestedByName', width: 24 },
  { header: 'Requested Date', key: 'requestedDate', width: 18 },
]);

const getPlainHeaderText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.map(getPlainHeaderText)
      .join(' ')
      .trim();
  }
  if (React.isValidElement(value)) return getPlainHeaderText(value.props?.children);

  return String(value || '').trim();
};

const isEffortDataField = (field = '') => (
  EFFORT_COLUMN_FIELDS.has(field) || String(field).startsWith('effort')
);

const isCostDataField = (field = '') => (
  COST_COLUMN_FIELDS.has(field) || String(field).startsWith('cost')
);

const normalizeExportColumnWidth = (width) => {
  const numericWidth = Number(width);

  if (!Number.isFinite(numericWidth) || numericWidth <= 0) return 18;
  if (numericWidth > 45) return Math.min(Math.max(Math.round(numericWidth / 8), 12), 42);

  return Math.min(Math.max(Math.round(numericWidth), 12), 42);
};

const getBcrExportColumnsFromConfig = ({
  apiTableConfiguration,
  canViewEffort,
  canViewCost,
}) => {
  const configuredColumns = Array.isArray(apiTableConfiguration)
    ? apiTableConfiguration
      .filter((column) => !['selection', 'action'].includes(column?.field))
      .filter((column) => column?.visible !== false)
      .filter((column) => (
        (canViewEffort || !isEffortDataField(column?.field))
        && (canViewCost || !isCostDataField(column?.field))
      ))
      .map((column) => ({
        header: getPlainHeaderText(column?.header) || column?.field,
        key: column?.field,
        width: normalizeExportColumnWidth(column?.width),
      }))
      .filter((column) => column.key)
    : [];

  return configuredColumns.length
    ? configuredColumns
    : getBcrExportColumns({ canViewEffort, canViewCost });
};

const getBcrRowsFromResponse = (response = {}) => (
  response?.data?.data?.tableData
  || response?.data?.tableData
  || []
);

const getBcrConfigFromResponse = (response = {}) => (
  response?.data?.data?.tableConfiguration
  || response?.data?.tableConfiguration
  || []
);

const getBcrPaginationFromResponse = (response = {}) => (
  response?.data?.data?.pagination
  || response?.data?.pagination
  || response?.data?.data?.paginationConfig
  || response?.data?.paginationConfig
  || response?.data?.data?.meta
  || response?.data?.meta
  || {}
);

const mapBcrRowForExport = (row = {}, columnsConfig = []) => {
  const mappedRow = mapTableRowForDisplay(row);

  if (Array.isArray(columnsConfig) && columnsConfig.length > 0) {
    return columnsConfig.reduce((acc, column) => ({
      ...acc,
      [column.key]: sanitizeText(mappedRow?.[column.key], '-'),
    }), {});
  }

  return {
    eern: sanitizeText(mappedRow?.eern, '-'),
    version: sanitizeText(mappedRow?.version, '-'),
    reasonForVersionChange: sanitizeText(mappedRow?.reasonForVersionChange, '-'),
    projectName: sanitizeText(mappedRow?.projectName, '-'),
    projectVertical: sanitizeText(mappedRow?.projectVertical, '-'),
    requestStatus: sanitizeText(mappedRow?.requestStatus, '-'),
    approverID: sanitizeText(mappedRow?.approverID, '-'),
    approverName: sanitizeText(mappedRow?.approverName, '-'),
    approvalDate: sanitizeText(mappedRow?.approvalDate, '-'),
    approvalRejectComment: sanitizeText(mappedRow?.approvalRejectComment, '-'),
    totalEffortHrs: sanitizeText(mappedRow?.totalEffortHrs, '-'),
    effortBa: sanitizeText(mappedRow?.effortBa, '-'),
    effortArchitect: sanitizeText(mappedRow?.effortArchitect, '-'),
    effortBeDeveloper: sanitizeText(mappedRow?.effortBeDeveloper, '-'),
    effortFeDeveloper: sanitizeText(mappedRow?.effortFeDeveloper, '-'),
    effortEtlDeveloper: sanitizeText(mappedRow?.effortEtlDeveloper, '-'),
    totalCostInr: sanitizeText(mappedRow?.totalCostInr, '-'),
    costOthers: sanitizeText(mappedRow?.costOthers, '-'),
    requestedByID: sanitizeText(mappedRow?.requestedByID, '-'),
    requestedByName: sanitizeText(mappedRow?.requestedByName, '-'),
    requestedDate: sanitizeText(mappedRow?.requestedDate, '-'),
  };
};

const ExportButton = ({ onClick, isLoading }) => (
  <button
    type="button"
    className="p-button p-button-text p-button-icon-only budget-change-request-export-btn"
    onClick={onClick}
    aria-label="Download Excel"
    disabled={isLoading}
  >
    <span className={`pi ${isLoading ? 'pi-spinner pi-spin' : 'pi-download'}`} />
  </button>
);

const AddRequestButton = ({ onClick, disabled }) => (
  <span className="budget-change-request-add-btn-wrap">
    <button
      type="button"
      className="p-button p-button-text p-button-icon-only budget-change-request-add-btn"
      onClick={onClick}
      aria-label="Add New Request"
      disabled={disabled}
    >
      <span className="pi pi-plus" />
    </button>
    {!disabled ? (
      <span className="budget-change-request-add-btn__tooltip" role="tooltip">
        Add New Request
      </span>
    ) : null}
  </span>
);

const SelectionHeaderCell = ({ onToggleAll }) => {
  const { selectedRows, visibleSelectableRowIds } = useContext(BudgetChangeRequestSelectionContext);
  const allSelected = (
    visibleSelectableRowIds.length > 0
    && visibleSelectableRowIds.every((rowId) => selectedRows.includes(rowId))
  );

  return (
    <label className="budget-change-request-select-header" onClick={(event) => event.stopPropagation()}>
      <input
        type="checkbox"
        checked={allSelected}
        onChange={(event) => onToggleAll(event.target.checked)}
        aria-label="Select all rows"
        disabled={visibleSelectableRowIds.length === 0}
      />
    </label>
  );
};

const SelectionRowCell = ({
  rowId,
  eern,
  requestStatus,
  onToggle,
  disabled: isDisabled,
}) => {
  const { selectedRows } = useContext(BudgetChangeRequestSelectionContext);
  const checked = selectedRows.includes(rowId);
  const disabled = isDisabled || !isSelectableStatus(requestStatus);

  return (
    <div className="budget-change-request-select-cell" onClick={(event) => event.stopPropagation()}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onToggle(rowId, event.target.checked)}
        aria-label={`Select ${eern}`}
        disabled={disabled}
      />
    </div>
  );
};

const ActionRowCell = ({ rowData, canEdit, canDelete, onEdit, onDelete }) => (
  <div className="budget-change-request-actions-cell" onClick={(event) => event.stopPropagation()}>
    <button
      type="button"
      className="budget-change-request-action-btn"
      onClick={() => onEdit(rowData)}
      aria-label={`Edit ${rowData.eern}`}
      disabled={!canEdit}
    >
      <img src={EditActionIcon} alt="" />
    </button>
    <button
      type="button"
      className="budget-change-request-action-btn"
      onClick={() => onDelete(rowData)}
      aria-label={`Delete ${rowData.eern}`}
      disabled={!canDelete}
    >
      <img src={DeleteActionIcon} alt="" />
    </button>
  </div>
);

const StatusActionCell = ({
  rowData,
  onActionSelect,
  canApprove,
}) => {
  const statusStyle = getStatusStyleFromRow(rowData);
  const statusLabel = sanitizeText(rowData?.requestStatus, '-');
  const actionableStatus = canApprove && (isOpenStatus(statusLabel) || isDraftStatus(statusLabel));

  if (!actionableStatus) {
    return (
      <span
        className="budget-change-request-row-status__badge"
        style={{
          color: statusStyle.textColor,
          background: statusStyle.boxColor,
          fontWeight: statusStyle.fontWeight || '400',
        }}
      >
        {statusLabel}
      </span>
    );
  }

  return (
    <div
      className="budget-change-request-row-status"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="budget-change-request-row-status__trigger"
        style={{
          color: statusStyle.textColor,
          background: statusStyle.boxColor,
        }}
        onClick={() => onActionSelect(rowData)}
      >
        <span className="budget-change-request-row-status__label">{statusLabel}</span>
      </button>
    </div>
  );
};

const CostAnalysisReportModal = ({ onClose, onSubmit }) => (
  <div
    className="bcr-report-backdrop"
    onClick={(event) => event.target === event.currentTarget && onClose?.()}
  >
    <div className="bcr-report-modal">
      <div className="bcr-report-modal__header">
        <h3 className="bcr-report-modal__title">Cost Analysis Report</h3>
      </div>

      <div className="bcr-report-modal__body">
        <div className="bcr-report-table-wrap">
          <table className="bcr-report-table">
            <thead>
              <tr>
                <th>Cost Category</th>
                <th>Planned (Rs)</th>
                <th>Actual (Rs)</th>
                <th>Actual Difference (Rs)</th>
                <th>Re-estimated (Rs)</th>
                <th>Re-estimated Difference (Rs)</th>
              </tr>
            </thead>
            <tbody>
              {COST_ANALYSIS_REPORT_ROWS.map((row) => (
                <tr key={row.category}>
                  <td>{row.category}</td>
                  <td className="bcr-report-table__num">{row.planned}</td>
                  <td className="bcr-report-table__num">{row.actual}</td>
                  <td className={`bcr-report-table__num bcr-report-table__num--${row.actualDifferenceTone}`}>
                    {row.actualDifference}
                  </td>
                  <td className="bcr-report-table__num">{row.reEstimated}</td>
                  <td className={`bcr-report-table__num bcr-report-table__num--${row.reEstimatedDifferenceTone}`}>
                    {row.reEstimatedDifference}
                  </td>
                </tr>
              ))}
              <tr className="bcr-report-table__total-row">
                <td>Total</td>
                <td className="bcr-report-table__num">{COST_ANALYSIS_REPORT_TOTAL.planned}</td>
                <td className="bcr-report-table__num">{COST_ANALYSIS_REPORT_TOTAL.actual}</td>
                <td className={`bcr-report-table__num bcr-report-table__num--${COST_ANALYSIS_REPORT_TOTAL.actualDifferenceTone}`}>
                  {COST_ANALYSIS_REPORT_TOTAL.actualDifference}
                </td>
                <td className="bcr-report-table__num">{COST_ANALYSIS_REPORT_TOTAL.reEstimated}</td>
                <td className={`bcr-report-table__num bcr-report-table__num--${COST_ANALYSIS_REPORT_TOTAL.reEstimatedDifferenceTone}`}>
                  {COST_ANALYSIS_REPORT_TOTAL.reEstimatedDifference}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bcr-report-legend">
          <span className="bcr-report-legend__item">
            <span className="bcr-report-legend__dot bcr-report-legend__dot--positive" />
            Remaining
          </span>
          <span className="bcr-report-legend__item">
            <span className="bcr-report-legend__dot bcr-report-legend__dot--negative" />
            Over Utilization
          </span>
        </div>
      </div>

      <div className="bcr-report-modal__footer">
        <button type="button" className="bcr-report-btn bcr-report-btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="bcr-report-btn bcr-report-btn--primary" onClick={onSubmit}>
          Submit
        </button>
      </div>
    </div>
  </div>
);

const StatusUpdateButton = ({ disabled, onClick }) => (
  <button
    type="button"
    className="p-button p-button-text p-button-icon-only budget-change-request-status-update-btn"
    onClick={onClick}
    disabled={disabled}
    aria-label="Update selected request status"
    title="Update Status"
  >
    <span className="pi pi-check" aria-hidden="true" />
  </button>
);

const StatusUpdateModal = ({
  selectedCount,
  projectName,
  requestNumber,
  option,
  onOptionChange,
  comment,
  onCommentChange,
  onClose,
  onSave,
}) => {
  const isSingleRequest = Boolean(requestNumber);
  const isSaveDisabled = !option || comment.trim().length === 0;

  return (
    <div
      className="budget-change-request-comment-backdrop"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="budget-change-request-comment-modal">
        <div className="budget-change-request-comment-modal__header">Update Status</div>
        <div className="budget-change-request-comment-modal__body">
          {isSingleRequest ? (
            <div className="budget-change-request-comment-modal__details">
              <div className="budget-change-request-comment-modal__detail-card">
                <div className="budget-change-request-comment-modal__detail-label">Project Name</div>
                <div className="budget-change-request-comment-modal__detail-value">{projectName || '-'}</div>
              </div>
              <div className="budget-change-request-comment-modal__detail-card budget-change-request-comment-modal__detail-card--compact">
                <div className="budget-change-request-comment-modal__detail-label">Request Number</div>
                <div className="budget-change-request-comment-modal__detail-value">{requestNumber}</div>
              </div>
            </div>
          ) : (
            <div className="budget-change-request-comment-modal__summary">
              Request:
              {' '}
              <strong>{selectedCount} selected request{selectedCount > 1 ? 's' : ''}</strong>
            </div>
          )}
          <label className="budget-change-request-comment-modal__label" htmlFor="budget-change-request-status">
            Status*
          </label>
          <div className="budget-change-request-comment-modal__select-wrap">
            <select
              id="budget-change-request-status"
              className="budget-change-request-comment-modal__select"
              value={option?.value || ''}
              onChange={(event) => {
                const nextOption = BULK_ACTION_OPTIONS.find((item) => item.value === event.target.value) || null;
                onOptionChange(nextOption);
              }}
            >
              <option value="" disabled>Select status</option>
              {BULK_ACTION_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <label className="budget-change-request-comment-modal__label" htmlFor="budget-change-request-comment">
            Comments*
          </label>
          <textarea
            id="budget-change-request-comment"
            className="budget-change-request-comment-modal__input"
            value={comment}
            onChange={(event) => onCommentChange(event.target.value)}
            placeholder="Enter..."
            rows={4}
          />
        </div>
        <div className="budget-change-request-comment-modal__footer">
          <button
            type="button"
            className="budget-change-request-comment-modal__btn budget-change-request-comment-modal__btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="budget-change-request-comment-modal__btn budget-change-request-comment-modal__btn--primary"
            onClick={onSave}
            disabled={isSaveDisabled}
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({
  title,
  message,
  onClose,
  onConfirm,
}) => (
  <div
    className="budget-change-request-delete-backdrop"
    onClick={(event) => event.target === event.currentTarget && onClose()}
  >
    <div className="budget-change-request-delete-modal">
      <div className="budget-change-request-delete-modal__header">{title}</div>
      <div className="budget-change-request-delete-modal__body">
        <div className="budget-change-request-delete-modal__warning" aria-hidden="true" />
        <div className="budget-change-request-delete-modal__message">{message}</div>
      </div>
      <div className="budget-change-request-delete-modal__footer">
        <button
          type="button"
          className="budget-change-request-delete-modal__btn budget-change-request-delete-modal__btn--ghost"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="budget-change-request-delete-modal__btn budget-change-request-delete-modal__btn--danger"
          onClick={onConfirm}
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

const buildTableConfiguration = ({
  apiTableConfiguration,
  selectionHeader,
  showActionColumn,
  canViewEffort,
  canViewCost,
}) => {
  const stripHeaderSortControls = (menuItems = []) => (
    (Array.isArray(menuItems) ? menuItems : []).map((menuItem) => {
      const nextItems = [];

      for (const item of Array.isArray(menuItem?.items) ? menuItem.items : []) {
        if (item?.type === 'sort') {
          continue;
        }

        if (item?.separator === true) {
          const previousItem = nextItems[nextItems.length - 1];
          if (!previousItem || previousItem?.separator === true) {
            continue;
          }
        }

        nextItems.push(item);
      }

      while (nextItems[nextItems.length - 1]?.separator === true) {
        nextItems.pop();
      }

      return {
        ...menuItem,
        items: nextItems,
      };
    }).filter((menuItem) => Array.isArray(menuItem?.items) && menuItem.items.length > 0)
  );

  const cleanedConfiguration = Array.isArray(apiTableConfiguration)
    ? apiTableConfiguration
      .filter((column) => !['selection', 'action'].includes(column?.field))
      .filter((column) => (
        (canViewEffort || !EFFORT_COLUMN_FIELDS.has(column?.field))
        && (canViewCost || !COST_COLUMN_FIELDS.has(column?.field))
      ))
      .map((column) => {
        const isGlobalFilterField = GLOBAL_FILTER_FIELDS.has(column?.field);
        const nextMenuItems = stripHeaderSortControls(column?.menuItems)
          .filter((menuItem) => Array.isArray(menuItem?.items) && menuItem.items.length > 0);

        return {
          ...column,
          header: wrapNumericHeaderValue(column?.field, column?.header),
          filter: isGlobalFilterField,
          filterVisible: false,
          showFilterButtonInHeader: false,
          isGlobalFilter: isGlobalFilterField,
          menuItems: isGlobalFilterField
            ? nextMenuItems
            : [],
          className: [
            column?.className || '',
            isNumericColumnField(column?.field) ? 'budget-change-request-number-column' : '',
            TRUNCATED_TEXT_FIELDS.has(column?.field) ? 'budget-change-request-long-text-column' : '',
          ].filter(Boolean).join(' '),
          headerClassName: [
            column?.headerClassName || '',
            isNumericColumnField(column?.field) ? 'budget-change-request-number-column' : '',
            TRUNCATED_TEXT_FIELDS.has(column?.field) ? 'budget-change-request-long-text-column' : '',
          ].filter(Boolean).join(' '),
        };
      })
    : [];

  const requestStatusIndex = cleanedConfiguration.findIndex((column) => column?.field === 'requestStatus');
  const approvalCommentIndex = cleanedConfiguration.findIndex((column) => column?.field === 'approvalRejectComment');

  if (requestStatusIndex >= 0 && approvalCommentIndex >= 0 && approvalCommentIndex !== requestStatusIndex + 1) {
    const [approvalCommentColumn] = cleanedConfiguration.splice(approvalCommentIndex, 1);
    cleanedConfiguration.splice(requestStatusIndex + 1, 0, approvalCommentColumn);
  }

  return [
    {
      field: 'selection',
      header: selectionHeader,
      visible: true,
      sortable: false,
      filter: false,
      filterVisible: false,
      showFilterButtonInHeader: false,
      pinnable: false,
      isGlobalFilter: false,
      defaultPinned: true,
      width: COLUMN_WIDTHS.selection,
      menuItems: [],
    },
    ...cleanedConfiguration,
    {
      field: 'action',
      header: 'Action',
      visible: showActionColumn,
      sortable: false,
      filter: false,
      filterVisible: false,
      showFilterButtonInHeader: false,
      pinnable: false,
      width: COLUMN_WIDTHS.action,
      isGlobalFilter: false,
      className: 'budget-change-request-action-column',
      headerClassName: 'budget-change-request-action-column',
      menuItems: [],
    },
  ];
};

const mapTableRowForDisplay = (row = {}) => ({
  ...row,
  requestStatus: getEffectiveStatusLabel(row),
  requestStatusColor: getEffectiveStatusColor(row),
  approverName: sanitizeText(row?.approverName, '-'),
  approvalRejectComment: sanitizeText(row?.approvalRejectComment, '-'),
  requestedByName: sanitizeText(row?.requestedByName, '-'),
  approvalDate: formatDisplayDate(row?.approvalDate),
  requestedDate: formatDisplayDate(row?.requestedDate),
});

const buildBudgetChangeRequestRoute = (targetProjectId, currentSearch = '', targetProjectName = '') => {
  const params = new URLSearchParams(currentSearch || '');
  params.set('projectId', String(targetProjectId || '').trim());
  if (String(targetProjectName || '').trim()) {
    params.set('projectName', String(targetProjectName || '').trim());
  } else {
    params.delete('projectName');
  }
  return `/delivery/project-details/budget-change-request?${params.toString()}`;
};

const getStatusStyleFromRow = (row = {}) => {
  const textColor = getEffectiveStatusColor(row);

  if (!textColor || textColor === '-') {
    return {};
  }

  return {
    textColor,
    boxColor: hexToRgba(textColor, 0.12) || '#f3f4f6',
    fontWeight: '400',
  };
};

const wrapNumericHeaderValue = (field, value) => {
  if (!isNumericColumnField(field)) {
    return value;
  }

  return <span className="budget-change-request-number-header-value">{value}</span>;
};

const parseNumericDisplayValue = (value) => {
  const normalizedValue = String(value ?? '')
    .replace(/,/g, '')
    .trim();

  if (!normalizedValue || normalizedValue === '-') {
    return null;
  }

  const parsedValue = Number.parseFloat(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatSignedDeltaValue = (value) => {
  const absoluteValue = Math.abs(value);

  if (Number.isInteger(absoluteValue)) {
    return absoluteValue.toLocaleString('en-IN');
  }

  return absoluteValue.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1');
};

const calculateSignedDelta = (currentValue, previousValue) => {
  const currentEffort = parseNumericDisplayValue(currentValue);
  const previousEffort = parseNumericDisplayValue(previousValue);

  if (currentEffort === null || previousEffort === null) {
    return null;
  }

  const difference = Number((currentEffort - previousEffort).toFixed(2));

  return Math.abs(difference) < 0.01 ? 0 : difference;
};

const TotalDeltaDisplayValue = ({ value, delta, className }) => {
  const hasDelta = typeof delta === 'number' && Number.isFinite(delta) && delta !== 0;
  const toneClassName = hasDelta
    ? (delta > 0 ? 'budget-change-request-effort-delta--positive' : 'budget-change-request-effort-delta--negative')
    : 'budget-change-request-effort-delta--neutral';
  const indicator = hasDelta
    ? `${delta > 0 ? '+' : '-'}${formatSignedDeltaValue(delta)}`
    : '-';

  return (
    <span className={`budget-change-request-number-cell-value ${className}`}>
      <span className={`budget-change-request-effort-delta ${toneClassName}`}>
        {indicator}
      </span>
      <span className="budget-change-request-total-delta-cell__value">
        {value}
      </span>
    </span>
  );
};

TotalDeltaDisplayValue.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  delta: PropTypes.number,
  className: PropTypes.string,
};

TotalDeltaDisplayValue.defaultProps = {
  value: '-',
  delta: null,
  className: '',
};

const wrapNumericDisplayValue = (field, value, options = {}) => {
  if (!isNumericColumnField(field)) {
    return value;
  }

  if (field === 'totalEffortHrs') {
    return (
      <TotalDeltaDisplayValue
        value={value}
        delta={options.effortDelta}
        className="budget-change-request-total-effort-cell"
      />
    );
  }

  if (field === 'totalCostInr') {
    return (
      <TotalDeltaDisplayValue
        value={value}
        delta={options.costDelta}
        className="budget-change-request-total-cost-cell"
      />
    );
  }

  return <span className="budget-change-request-number-cell-value">{value}</span>;
};

const TruncatedText = ({ value, className = '' }) => {
  const displayValue = sanitizeText(value, '-');

  return (
    <span className={`bcr-truncate-wrap${className ? ` ${className}` : ''}`}>
      <span className="bcr-truncate">
        {displayValue}
      </span>
      {displayValue !== '-' && (
        <span className="bcr-truncate-tooltip" role="tooltip">
          {displayValue}
        </span>
      )}
    </span>
  );
};

const wrapTruncatedDisplayValue = (field, value) => {
  if (!TRUNCATED_TEXT_FIELDS.has(field)) {
    return value;
  }

  return <TruncatedText value={value} className="budget-change-request-truncated-text" />;
};

const buildEnrichedTableData = ({
  tableData,
  onToggleRow,
  onStatusActionSelect,
  onEditRow,
  onDeleteRow,
  canApprove,
  canEditRequest,
  canDeleteRequest,
}) =>
  tableData.map((row, index) => {
    const mappedRow = mapTableRowForDisplay(row);
    const previousVersionRow = tableData[index + 1];
    const previousVersionMappedRow = previousVersionRow
      ? mapTableRowForDisplay(previousVersionRow)
      : null;
    const effortDelta = calculateSignedDelta(
      mappedRow?.totalEffortHrs,
      previousVersionMappedRow?.totalEffortHrs,
    );
    const costDelta = calculateSignedDelta(
      mappedRow?.totalCostInr,
      previousVersionMappedRow?.totalCostInr,
    );
    const effectiveStatus = getEffectiveStatusLabel(row);
    const canEditRow = canEditRequest && isEditableStatus(effectiveStatus);
    const canDeleteRow = canDeleteRequest && isDeletableStatus(effectiveStatus);
    const displayRow = Object.keys(mappedRow).reduce((accumulator, field) => ({
      ...accumulator,
      [field]: wrapTruncatedDisplayValue(field, wrapNumericDisplayValue(field, mappedRow[field], {
        effortDelta,
        costDelta,
      })),
    }), {});

    return {
      ...displayRow,
      selection: (
        <SelectionRowCell
          rowId={row.id}
          eern={row.eern}
          requestStatus={effectiveStatus}
          onToggle={onToggleRow}
          disabled={!canApprove}
        />
      ),
      requestStatus: (
        <StatusActionCell
          rowData={row}
          onActionSelect={onStatusActionSelect}
          canApprove={canApprove}
        />
      ),
      action: (
        <ActionRowCell
          rowData={row}
          canEdit={canEditRow}
          canDelete={canDeleteRow}
          onEdit={onEditRow}
          onDelete={onDeleteRow}
        />
      ),
      rowConfiguration: {
        template: [],
        onClick: [{ field: 'eern' }],
      },
    };
  });

const buildToolbarElements = ({
  onOpenCostAnalysis,
  canViewCostAnalysis,
  canAddRequest,
  disableAddRequest,
  onAddRequest,
  selectedCount,
  onOpenStatusUpdate,
  canApproveRequest,
  onExport,
  isExporting,
}) => {
  return [
    canViewCostAnalysis ? (
      <button
        key="cost-analysis-report"
        type="button"
        className="budget-toolbar-link"
        onClick={onOpenCostAnalysis}
      >
        Cost Analysis Report
      </button>
    ) : null,
    canApproveRequest ? (
      <StatusUpdateButton
        key="status-update-action"
        disabled={selectedCount === 0}
        onClick={onOpenStatusUpdate}
      />
    ) : null,
    canAddRequest ? <AddRequestButton key="add-budget-change-request" onClick={onAddRequest} disabled={disableAddRequest} /> : null,
    <ExportButton key="download-budget-change-request" onClick={onExport} isLoading={isExporting} />,
  ];
};

TruncatedText.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
};

TruncatedText.defaultProps = {
  value: '',
  className: '',
};

StatusUpdateButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

StatusUpdateButton.defaultProps = {
  disabled: false,
};

StatusUpdateModal.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  projectName: PropTypes.string,
  requestNumber: PropTypes.string,
  option: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
  onOptionChange: PropTypes.func.isRequired,
  comment: PropTypes.string.isRequired,
  onCommentChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

StatusUpdateModal.defaultProps = {
  projectName: '',
  requestNumber: '',
  option: null,
};

DeleteConfirmModal.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

DeleteConfirmModal.defaultProps = {
  title: 'Delete Request',
  message: 'Are you sure you want to delete this request?',
};

ExportButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

AddRequestButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

AddRequestButton.defaultProps = {
  disabled: false,
};

const BudgetChangeRequestTable = ({ projectId, projectName }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { snackbarOpen } = useSnackbar();
  const resolvedProfile = useMemo(
    () => (userProfile && !Array.isArray(userProfile) ? userProfile : null),
    [userProfile],
  );
  const accessToken = resolvedProfile?.access_token || '';
  const permissions = useMemo(
    () => (resolvedProfile?.permissions && typeof resolvedProfile.permissions === 'object'
      ? resolvedProfile.permissions
      : {}),
    [resolvedProfile?.permissions],
  );
  const canViewApproval = hasAnyPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_APPROVE,
  );
  const canApproveRequest = hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_APPROVE,
    'update',
  );
  const canViewEffort = hasAnyPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_EFFORTS,
  ) || canViewApproval;
  const canViewCost = hasAnyPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_COST,
  ) || canViewApproval;
  const canEditEffort = hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_EFFORTS,
    'create',
  ) || hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_EFFORTS,
    'update',
  );
  const canEditCost = hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_COST,
    'create',
  ) || hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_COST,
    'update',
  );
  const canAddRequest = hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_EFFORTS,
    'create',
  ) || hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_COST,
    'create',
  );
  const canEditRequest = canEditEffort || canEditCost;
  const canDeleteRequest = hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_EFFORTS,
    'delete',
  ) || hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_BUDGET_CHANGE_REQUEST_COST,
    'delete',
  );

  const [selectedRows, setSelectedRows] = useState([]);
  const [modalState, setModalState] = useState(null);
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const [isStatusActionSubmitting, setIsStatusActionSubmitting] = useState(false);
  const [isCostAnalysisOpen, setIsCostAnalysisOpen] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [resourceSkillOptions, setResourceSkillOptions] = useState([]);
  const [costCategoryOptions, setCostCategoryOptions] = useState([]);
  const [detailCacheById, setDetailCacheById] = useState({});
  const [visibleSelectableRowIds, setVisibleSelectableRowIds] = useState([]);
  const [bulkActionDialog, setBulkActionDialog] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [isPendingRequestStatusLoading, setIsPendingRequestStatusLoading] = useState(false);
  const [deleteConfirmRow, setDeleteConfirmRow] = useState(null);
  const [activeProject, setActiveProject] = useState(() => ({
    projectId: toProjectId(projectId),
    projectName: sanitizeText(projectName, ''),
  }));
  const appliedProjectContextRef = useRef({
    projectId: toProjectId(projectId),
    projectName: sanitizeText(projectName, ''),
  });
  const visibleProjectContextRef = useRef({
    projectId: toProjectId(projectId),
    projectName: sanitizeText(projectName, ''),
  });
  const exportRowsRef = useRef([]);
  const exportProjectNameRef = useRef(projectName);
  const projectNameFilterOptionsRef = useRef([]);
  const projectNameMasterOptionsRef = useRef([]);
  const didSeedRouteDefaultsRef = useRef(false);
  const routeScopedDefaultPayload = useMemo(
    () => buildDefaultAdditionalPayload({
      projectId: toProjectId(projectId),
      projectName: sanitizeText(projectName, ''),
    }),
    [projectId, projectName],
  );
  const setAppliedProjectContext = useCallback((nextProject = {}) => {
    const normalizedProjectId = toProjectId(nextProject?.projectId);
    const normalizedProjectName = sanitizeText(nextProject?.projectName, '');

    if (!normalizedProjectId && !normalizedProjectName) {
      return appliedProjectContextRef.current;
    }

    const currentProject = appliedProjectContextRef.current || {};
    const resolvedProject = {
      projectId: normalizedProjectId || currentProject.projectId || toProjectId(projectId),
      projectName: normalizedProjectName || currentProject.projectName || sanitizeText(projectName, ''),
    };

    appliedProjectContextRef.current = resolvedProject;
    setActiveProject((current) => (
      current.projectId === resolvedProject.projectId
        && current.projectName === resolvedProject.projectName
        ? current
        : resolvedProject
    ));

    return resolvedProject;
  }, [projectId, projectName]);
  const buildRouteDefaultPayload = useCallback(
    () => buildDefaultAdditionalPayload({
      projectId: appliedProjectContextRef.current?.projectId || activeProject.projectId || toProjectId(projectId),
      projectName: appliedProjectContextRef.current?.projectName || activeProject.projectName || sanitizeText(projectName, ''),
    }),
    [activeProject.projectId, activeProject.projectName, projectId, projectName],
  );
  const additionalPayloadDataRef = useRef({ ...routeScopedDefaultPayload });
  const draftFilterPayloadRef = useRef({});
  const [additionalPayloadData, setAdditionalPayloadData] = useState(routeScopedDefaultPayload);
  const syncAdditionalPayloadDataRef = useCallback((nextPayload = {}, shouldUpdateState = true) => {
    const resetProjectContext = {
      projectId: appliedProjectContextRef.current?.projectId || activeProject.projectId || toProjectId(projectId),
      projectName: appliedProjectContextRef.current?.projectName || activeProject.projectName || sanitizeText(projectName, ''),
    };
    const selectedProjectOption = getSingleSelectFilterValue(nextPayload?.projectName);
    const selectedProjectName = getProjectSelectionName(selectedProjectOption);
    const currentScopedProjectName = getProjectSelectionName(resetProjectContext.projectName);
    const selectedProjectContext = selectedProjectOption
      ? getProjectContextFromSelection(
        selectedProjectOption,
        projectNameFilterOptionsRef.current,
      )
      : {};
    let scopedProjectId = toProjectId(nextPayload?.project_id);

    if (selectedProjectName) {
      if (selectedProjectContext.projectId) {
        scopedProjectId = selectedProjectContext.projectId;
      } else if (
        scopedProjectId
        && isSameProjectSelectionName(selectedProjectName, currentScopedProjectName)
      ) {
        scopedProjectId = Number(scopedProjectId);
      } else if (!isSameProjectSelectionName(selectedProjectName, currentScopedProjectName)) {
        scopedProjectId = null;
      } else {
        scopedProjectId = resetProjectContext.projectId;
      }
    } else if (!scopedProjectId) {
      scopedProjectId = resetProjectContext.projectId;
    }

    const payloadWithFilters = {
      ...nextPayload,
      filter: buildRequestFilterArray(nextPayload),
    };
    delete payloadWithFilters.project_id;
    delete payloadWithFilters.project_name;
    if (selectedProjectName) {
      payloadWithFilters.project_name = selectedProjectName;
    }
    if (scopedProjectId) {
      payloadWithFilters.project_id = Number(scopedProjectId);
    }

    const stablePayload = additionalPayloadDataRef.current || {};

    Object.keys(stablePayload).forEach((key) => {
      delete stablePayload[key];
    });
    Object.assign(stablePayload, payloadWithFilters);

    if (shouldUpdateState) {
      setAdditionalPayloadData((current) => (
        arePayloadsEqual(current, stablePayload)
          ? current
          : { ...stablePayload }
      ));
    }

    return stablePayload;
  }, [activeProject.projectId, activeProject.projectName, projectId, projectName]);
  const loadProjectNameMasterOptions = useCallback(async () => {
    if (projectNameMasterOptionsRef.current.length > 0) {
      return projectNameMasterOptionsRef.current;
    }

    const response = await UseAxios(masterProjectNameData());
    const normalizedOptions = normalizeProjectNameMasterOptions(getProjectNameMasterRows(response));
    if (normalizedOptions.length > 0) {
      projectNameMasterOptionsRef.current = normalizedOptions;
    }

    return projectNameMasterOptionsRef.current;
  }, []);
  const resolveProjectContextWithFallback = useCallback(async (selectedValue) => {
    const selectedProjectContext = getProjectContextFromSelection(
      selectedValue,
      projectNameFilterOptionsRef.current,
    );
    if (selectedProjectContext.projectId) {
      return selectedProjectContext;
    }

    const selectedProjectName = getProjectSelectionName(selectedValue);
    if (!selectedProjectName) {
      return selectedProjectContext;
    }

    const masterOptions = await loadProjectNameMasterOptions();
    const matchedMasterOption = masterOptions.find((option) => (
      sanitizeText(option?.name ?? option?.label ?? option?.value, '').toLowerCase()
      === selectedProjectName.toLowerCase()
    ));

    if (!matchedMasterOption) {
      return selectedProjectContext;
    }

    return getProjectContextFromSelection(
      matchedMasterOption,
      masterOptions,
    );
  }, [loadProjectNameMasterOptions]);
  const getCurrentActionProjectId = useCallback(
    () => visibleProjectContextRef.current?.projectId
      || toProjectId(additionalPayloadDataRef.current?.project_id)
      || appliedProjectContextRef.current?.projectId
      || activeProject.projectId,
    [activeProject.projectId],
  );
  const additionalPayloadKey = useMemo(
    () => JSON.stringify(additionalPayloadData || {}),
    [additionalPayloadData],
  );
  const tableKey = useMemo(
    () => [
      'budget-change-request',
      activeProject.projectId,
      refreshNonce,
      additionalPayloadKey,
    ].filter(Boolean).join('-'),
    [activeProject.projectId, additionalPayloadKey, refreshNonce],
  );

  useEffect(() => {
    if (didSeedRouteDefaultsRef.current) {
      return;
    }

    didSeedRouteDefaultsRef.current = true;
    setAppliedProjectContext({
      projectId: toProjectId(projectId),
      projectName: sanitizeText(projectName, ''),
    });

    if (!arePayloadsEqual(additionalPayloadDataRef.current, routeScopedDefaultPayload)) {
      syncAdditionalPayloadDataRef(routeScopedDefaultPayload);
    }
  }, [projectId, projectName, routeScopedDefaultPayload, setAppliedProjectContext, syncAdditionalPayloadDataRef]);

  useEffect(() => {
    let isMounted = true;

    const loadMasters = async () => {
      const [resourceResponse, costCategoryResponse] = await Promise.all([
        UseAxios(getBudgetChangeRequestResourceTypes()),
        UseAxios(getBudgetChangeRequestCostCategories()),
      ]);

      if (!isMounted) return;

      if (resourceResponse?.status === 200) {
        const rows = resourceResponse?.data?.data?.resourceTypes || resourceResponse?.data?.resourceTypes || [];
        setResourceSkillOptions(normalizeResourceSkillOptions(rows));
      } else {
        snackbarOpen('Unable to load resource skills.', 'error');
      }

      if (costCategoryResponse?.status === 200) {
        const rows = costCategoryResponse?.data?.data?.costCategories || costCategoryResponse?.data?.costCategories || [];
        setCostCategoryOptions(normalizeCostCategoryOptions(rows));
      } else {
        snackbarOpen('Unable to load cost categories.', 'error');
      }
    };

    loadMasters();

    return () => {
      isMounted = false;
    };
  }, [snackbarOpen]);

  const fetchPendingRequestStateForProject = useCallback(async (targetProjectId) => {
    if (!targetProjectId) {
      return false;
    }

    const response = await UseAxios(listBudgetChangeRequests({
      project_id: Number(targetProjectId),
      page: 1,
      limit: 10000,
      filters: [],
      selectedViewId: null,
      searchQuery: '',
      userInfo: {
        ...(resolvedProfile || {}),
        globalColors: resolvedProfile?.siteConfiguration?.global_colors,
      },
    }));

    const allRows = response?.data?.data?.tableData
      || response?.data?.tableData
      || [];

    return Array.isArray(allRows) && allRows.some((row) => isSelectableStatus(getEffectiveStatusLabel(row)));
  }, [resolvedProfile]);

  useEffect(() => {
    const syncDateRangeField = (field, range = [null, null]) => {
      const defaultDateRange = buildDefaultNinetyDayDateRange();
      const defaultRange = [defaultDateRange.from, defaultDateRange.to];
      const nextRange = hasCompleteDateRange(range)
        ? normalizeDateRangeArray(range)
        : defaultRange;
      const nextPayload = {
        ...additionalPayloadDataRef.current,
        [field]: {
          ...(additionalPayloadDataRef.current?.[field] || {}),
          selectedValues: hasCompleteDateRange(nextRange) ? [...nextRange] : [...defaultRange],
          range: [...nextRange],
        },
      };

      syncAdditionalPayloadDataRef(nextPayload);
    };

    const syncVisibleDateRangePanels = (rootNode = document) => {
      const visiblePanels = getVisibleDateRangePanels(rootNode);
      if (!visiblePanels.length) {
        return false;
      }

      let hasSyncedDateRange = false;

      visiblePanels.forEach((panelElement) => {
        const visibleDateRange = getDateRangeValuesFromRoot(panelElement);
        if (!hasCompleteDateRange(visibleDateRange)) return;

        const field = resolveDateRangeFieldName(panelElement, rootNode);
        if (!field) return;

        syncDateRangeField(field, visibleDateRange);
        hasSyncedDateRange = true;
      });

      return hasSyncedDateRange;
    };

    const syncVisibleSingleSelectPanels = (rootNode = document) => {
      let nextPayload = {
        ...additionalPayloadDataRef.current,
        ...draftFilterPayloadRef.current,
      };
      let hasSyncedSingleSelect = false;

      getVisibleSingleSelectPanels(rootNode).forEach((panelElement) => {
        const field = resolveSingleSelectFieldName(panelElement, rootNode);

        if (!field) return;

        const selectedOptionValue = getVisibleSingleSelectValue(panelElement);
        const selectedOption = getNormalizedFilterOption(selectedOptionValue);
        const selectedProjectContext = field === 'projectName'
          ? getProjectContextFromSelection(
            selectedOption,
            projectNameFilterOptionsRef.current,
          )
          : {};

        nextPayload = {
          ...nextPayload,
          ...(field === 'projectName' && selectedProjectContext.projectId
            ? { project_id: selectedProjectContext.projectId }
            : {}),
          [field]: {
            ...(nextPayload?.[field] || {}),
            selectedValue: selectedOption,
            selectedValues: selectedOption ? [selectedOption] : [],
          },
        };
        hasSyncedSingleSelect = true;
      });

      if (hasSyncedSingleSelect) {
        syncAdditionalPayloadDataRef(nextPayload);
      }

      return hasSyncedSingleSelect;
    };

    const syncVisibleMultiSelectPanels = (rootNode = document) => {
      let nextPayload = {
        ...additionalPayloadDataRef.current,
        ...draftFilterPayloadRef.current,
      };
      let hasSyncedMultiSelect = false;

      getVisibleMultiSelectPanels(rootNode).forEach((panelElement) => {
        const field = resolveMultiSelectFieldName(panelElement, rootNode);
        if (!field) return;

        nextPayload = {
          ...nextPayload,
          [field]: {
            ...(nextPayload?.[field] || {}),
            selectedValues: getNormalizedFilterOptions(getVisibleMultiSelectValues(panelElement)),
          },
        };
        hasSyncedMultiSelect = true;
      });

      if (hasSyncedMultiSelect) {
        syncAdditionalPayloadDataRef(nextPayload);
      }

      return hasSyncedMultiSelect;
    };

    const updateDraftFilterPayload = (nextDraftPayload = {}) => {
      draftFilterPayloadRef.current = {
        ...draftFilterPayloadRef.current,
        ...nextDraftPayload,
      };
    };

    const syncRouteToProject = (nextProjectId, nextProjectName = '') => {
      const normalizedProjectId = toProjectId(nextProjectId);
      const normalizedProjectName = sanitizeText(nextProjectName, '');
      const hasSameProjectId = normalizedProjectId === toProjectId(projectId);
      const hasSameProjectName = normalizedProjectName === sanitizeText(projectName, '');

      if (!normalizedProjectId || (hasSameProjectId && hasSameProjectName)) {
        return;
      }

      const nextUrl = buildBudgetChangeRequestRoute(
        normalizedProjectId,
        location.search,
        normalizedProjectName,
      );

      if (typeof window !== 'undefined' && window.history?.replaceState) {
        window.history.replaceState(window.history.state, '', nextUrl);
      }

      navigate(nextUrl, {
        replace: true,
      });
    };

    const syncDraftFilters = async (event) => {
      const buttonElement = event.target?.closest?.('button');
      const footerContainer = event.target?.closest?.('.tu-filter-panel-footer');
      const singleSelectContainer = event.target?.closest?.('.tu-single-select-filter');
      const multiSelectContainer = event.target?.closest?.('.tu-multi-select-filter');
      const dateRangeContainer = event.target?.closest?.(
        '.tu-value-condition-filter, .calendar-filter-container, .tu-filter-panel-template, .tu-filter-detail-template',
      );
      const buttonLabel = String(buttonElement?.textContent || '').trim();
      const isApplyButton = buttonLabel === 'Apply';
      const isResetButton = buttonLabel === 'Reset';
      const activeDateRangeField = resolveDateRangeFieldName(dateRangeContainer, document);
      const isMainApply = Boolean(
        isApplyButton
        && footerContainer
        && !singleSelectContainer
        && !multiSelectContainer
        && !dateRangeContainer,
      );
      const activeSingleSelectField = resolveSingleSelectFieldName(singleSelectContainer, document);
      const activeMultiSelectField = resolveMultiSelectFieldName(multiSelectContainer, document);
      const isProjectNameApply = Boolean(
        isApplyButton
        && singleSelectContainer
        && activeSingleSelectField === 'projectName',
      );
      const isApproverNameApply = Boolean(
        isApplyButton
        && singleSelectContainer
        && activeSingleSelectField === 'approverName',
      );
      const isRequestedByApply = Boolean(
        isApplyButton
        && singleSelectContainer
        && activeSingleSelectField === 'requestedByName',
      );
      const isRequestStatusApply = Boolean(
        isApplyButton
        && multiSelectContainer
        && activeMultiSelectField === 'requestStatus',
      );
      const isDateRangeApply = Boolean(
        isApplyButton
        && dateRangeContainer
        && activeDateRangeField,
      );
      const isDateRangeReset = Boolean(
        isResetButton
        && dateRangeContainer
        && activeDateRangeField,
      );
      const isMainReset = Boolean(
        isResetButton
        && footerContainer
        && !singleSelectContainer
        && !multiSelectContainer,
      );

      if (isMainReset) {
        event.preventDefault();
        event.stopPropagation();
        draftFilterPayloadRef.current = {};
        syncAdditionalPayloadDataRef(buildRouteDefaultPayload());
        syncRouteToProject(activeProject.projectId, activeProject.projectName);
        setSelectedRows([]);
        setVisibleSelectableRowIds([]);
        setPagination((current) => ({
          ...current,
          page: 1,
        }));
        setRefreshNonce((current) => current + 1);
        return;
      }

      if (isMainApply) {
        event.preventDefault();
        event.stopPropagation();
        syncVisibleSingleSelectPanels(document);
        syncVisibleMultiSelectPanels(document);
        syncVisibleDateRangePanels(document);
        const appliedProjectSelection = getSingleSelectFilterValue(
          draftFilterPayloadRef.current?.projectName
          ?? additionalPayloadDataRef.current?.projectName,
        );
        const appliedProjectContext = await resolveProjectContextWithFallback(appliedProjectSelection);
        const appliedProjectName = getProjectSelectionName(appliedProjectSelection);
        const shouldRetainCurrentProjectRoute = isSameProjectSelectionName(
          appliedProjectSelection,
          additionalPayloadDataRef.current?.projectName
          || activeProject.projectName,
        );
        if (appliedProjectContext.projectId) {
          setAppliedProjectContext({
            projectId: appliedProjectContext.projectId,
            projectName: appliedProjectContext.projectName || appliedProjectName,
          });
        }
        if (Object.keys(draftFilterPayloadRef.current || {}).length) {
          syncAdditionalPayloadDataRef({
            ...additionalPayloadDataRef.current,
            ...draftFilterPayloadRef.current,
          });
          draftFilterPayloadRef.current = {};
        }
        syncRouteToProject(
          appliedProjectContext.projectId
          || (
            shouldRetainCurrentProjectRoute
              ? additionalPayloadDataRef.current?.project_id
              : null
          ),
          appliedProjectContext.projectName || appliedProjectName,
        );
        setSelectedRows([]);
        setPagination((current) => (
          current.page === 1
            ? current
            : {
              ...current,
              page: 1,
            }
        ));
        setRefreshNonce((current) => current + 1);
        return;
      }

      if (isProjectNameApply || isApproverNameApply || isRequestedByApply) {
        const selectedOptionValue = getVisibleSingleSelectValue(singleSelectContainer);
        const selectedOption = getNormalizedFilterOption(selectedOptionValue);
        const field = resolveSingleSelectFieldName(singleSelectContainer, document);
        const selectedProjectContext = isProjectNameApply
          ? await resolveProjectContextWithFallback(selectedOption)
          : {};

        const nextPayload = {
          ...draftFilterPayloadRef.current,
          ...(isProjectNameApply && selectedProjectContext.projectId
            ? { project_id: selectedProjectContext.projectId }
            : {}),
          [field]: {
            ...(additionalPayloadDataRef.current?.[field] || {}),
            ...(draftFilterPayloadRef.current?.[field] || {}),
            selectedValue: selectedOption,
            selectedValues: selectedOption ? [selectedOption] : [],
          },
        };

        updateDraftFilterPayload(nextPayload);
        return;
      }

      if (isRequestStatusApply) {
        const selectedValues = getVisibleMultiSelectValues(multiSelectContainer);
        const field = resolveMultiSelectFieldName(multiSelectContainer, document);
        const nextPayload = {
          ...draftFilterPayloadRef.current,
          [field]: {
            ...(additionalPayloadDataRef.current?.[field] || {}),
            ...(draftFilterPayloadRef.current?.[field] || {}),
            selectedValues: getNormalizedFilterOptions(selectedValues),
          },
        };

        updateDraftFilterPayload(nextPayload);
        return;
      }

      if (isDateRangeReset) {
        const defaultDateRange = buildDefaultNinetyDayDateRange();
        updateDraftFilterPayload({
          [activeDateRangeField]: {
            ...(additionalPayloadDataRef.current?.[activeDateRangeField] || {}),
            ...(draftFilterPayloadRef.current?.[activeDateRangeField] || {}),
            selectedValues: [defaultDateRange.from, defaultDateRange.to],
            range: [defaultDateRange.from, defaultDateRange.to],
          },
        });
        return;
      }

      if (!isDateRangeApply) return;

      const visibleDateRange = getDateRangeValuesFromRoot(dateRangeContainer || document);
      if (!hasCompleteDateRange(visibleDateRange)) return;
      updateDraftFilterPayload({
        [activeDateRangeField]: {
          ...(additionalPayloadDataRef.current?.[activeDateRangeField] || {}),
          ...(draftFilterPayloadRef.current?.[activeDateRangeField] || {}),
          selectedValues: [...visibleDateRange],
          range: [...visibleDateRange],
        },
      });
    };

    document.addEventListener('click', syncDraftFilters, true);

    return () => {
      document.removeEventListener('click', syncDraftFilters, true);
    };
  }, [
    buildRouteDefaultPayload,
    location.search,
    navigate,
    projectId,
    activeProject.projectId,
    activeProject.projectName,
    resolveProjectContextWithFallback,
    syncAdditionalPayloadDataRef,
  ]);

  const handleToggleRow = useCallback((rowId, checked) => {
    setSelectedRows((current) => {
      if (checked) {
        return current.includes(rowId) ? current : [...current, rowId];
      }

      return current.filter((id) => id !== rowId);
    });
  }, []);

  useEffect(() => {
    if (selectedRows.length === 0 && bulkActionDialog?.clearSelectionOnSave) {
      setBulkActionDialog(null);
    }
  }, [bulkActionDialog, selectedRows.length]);

  const openModalWithDetail = useCallback(async (mode, rowData) => {
    if (!canViewEffort && !canViewCost) {
      snackbarOpen('You do not have access to view budget change request details.', 'error');
      return;
    }

    const resolvedRequestStatus = typeof rowData?.requestStatus === 'string'
      ? rowData.requestStatus
      : getEffectiveStatusLabel(rowData);
    const cachedDetail = detailCacheById[rowData.id];
    if (cachedDetail) {
      setModalState({
        mode,
        eern: cachedDetail?.header?.eern || rowData?.eern || '',
        rowId: rowData.id,
        requestStatus: cachedDetail?.header?.requestStatus || resolvedRequestStatus || '',
        initialData: cachedDetail,
      });
      return;
    }

    const response = await UseAxios(getBudgetChangeRequestById(rowData.id));
    if (response?.status !== 200) {
      snackbarOpen(getApiMessage(response, 'Unable to load budget change request details.'), 'error');
      return;
    }

    const detailPayload = response?.data?.data || response?.data?.budgetChangeRequest || {};
    const fallbackReason = typeof rowData?.reasonForVersionChange === 'string'
      ? rowData.reasonForVersionChange
      : '';
    const normalizedDetail = normalizeDetailPayload(detailPayload, fallbackReason);

    setModalState({
      mode,
      eern: normalizedDetail?.header?.eern || rowData?.eern || '',
      rowId: rowData.id,
      requestStatus: normalizedDetail?.header?.requestStatus || resolvedRequestStatus || '',
      initialData: normalizedDetail,
    });
  }, [canViewCost, canViewEffort, detailCacheById, snackbarOpen]);

  const handleAddNew = useCallback(async () => {
    if (!canAddRequest) {
      snackbarOpen('You do not have access to create budget change requests.', 'error');
      return;
    }

    const targetProjectId = getCurrentActionProjectId();
    if (!targetProjectId) {
      snackbarOpen('Unable to resolve project context for this request.', 'error');
      return;
    }

    setIsPendingRequestStatusLoading(true);
    const nextHasPendingRequest = await fetchPendingRequestStateForProject(targetProjectId);
    setHasPendingRequest(nextHasPendingRequest);
    setIsPendingRequestStatusLoading(false);

    if (nextHasPendingRequest) {
      snackbarOpen('Only one Draft or Open request is allowed for a project.', 'error');
      return;
    }

    setModalState({
      mode: 'add',
      eern: '',
      rowId: null,
      requestStatus: '',
      initialData: {
        effortRows: [],
        costRows: [],
        reasonForVersionChange: '',
      },
    });
  }, [canAddRequest, fetchPendingRequestStateForProject, getCurrentActionProjectId, snackbarOpen]);

  const handleDeleteRow = useCallback((rowData) => {
    if (!canDeleteRequest) {
      snackbarOpen('You do not have access to delete budget change requests.', 'error');
      return;
    }

    setDeleteConfirmRow(rowData);
  }, [canDeleteRequest, snackbarOpen]);

  const handleConfirmDelete = useCallback(async () => {
    const rowId = deleteConfirmRow?.id;
    if (!rowId) {
      setDeleteConfirmRow(null);
      return;
    }
    const response = await UseAxios(deleteBudgetChangeRequest(rowId));

    if (response?.status !== 200) {
      snackbarOpen(getApiMessage(response, 'Unable to delete budget change request.'), 'error');
      return;
    }

    snackbarOpen(getApiMessage(response, 'Budget change request cancelled.'), 'success');
    setSelectedRows((current) => current.filter((id) => id !== rowId));
    setDetailCacheById((current) => {
      if (!current[rowId]) return current;

      const next = { ...current };
      delete next[rowId];
      return next;
    });
    setDeleteConfirmRow(null);
    setRefreshNonce((current) => current + 1);
  }, [deleteConfirmRow, snackbarOpen]);

  const handleCellClick = useCallback(({ fieldPath, rowData }) => {
    if (fieldPath === 'eern') {
      openModalWithDetail('view', rowData);
    }
  }, [openModalWithDetail]);

  const handleOpenBulkStatusUpdate = useCallback(() => {
    if (!canApproveRequest || selectedRows.length === 0) {
      return;
    }

    setBulkActionDialog({
      option: null,
      comment: '',
      targetIds: selectedRows,
      clearSelectionOnSave: true,
      projectName: '',
      requestNumber: '',
    });
  }, [canApproveRequest, selectedRows]);

  const handleRowStatusActionSelect = useCallback((rowData) => {
    if (!canApproveRequest) {
      return;
    }

    setBulkActionDialog({
      option: null,
      comment: '',
      targetIds: rowData?.id ? [rowData.id] : [],
      clearSelectionOnSave: false,
      projectName: sanitizeText(rowData?.projectName, activeProject.projectName || projectName || '-'),
      requestNumber: sanitizeText(rowData?.eern, '-'),
    });
  }, [activeProject.projectName, canApproveRequest, projectName]);

  const handleBulkActionSave = useCallback(async () => {
    const targetIds = Array.isArray(bulkActionDialog?.targetIds)
      ? bulkActionDialog.targetIds.filter((id) => id !== null && id !== undefined)
      : [];

    if (!bulkActionDialog?.option || targetIds.length === 0) {
      return;
    }

    const response = await UseAxios(bulkUpdateBudgetChangeRequestStatus({
      ids: targetIds,
      action: bulkActionDialog.option.value,
      comment: bulkActionDialog.comment,
    }));

    if (response?.status !== 200) {
      snackbarOpen(getApiMessage(response, 'Unable to update selected budget change requests.'), 'error');
      return;
    }

    setBulkActionDialog(null);
    if (bulkActionDialog?.clearSelectionOnSave) {
      setSelectedRows([]);
    }
    setRefreshNonce((current) => current + 1);
    snackbarOpen(
      getApiMessage(response, `${bulkActionDialog.option.label} applied successfully.`),
      'success',
    );
  }, [bulkActionDialog, snackbarOpen]);

  const handleModalStatusSubmit = useCallback(async ({ option, comment }) => {
    if (!canApproveRequest || !modalState?.rowId || !option?.value) {
      return;
    }

    setIsStatusActionSubmitting(true);
    try {
      const response = await UseAxios(bulkUpdateBudgetChangeRequestStatus({
        ids: [modalState.rowId],
        action: option.value,
        comment,
      }));

      if (response?.status !== 200) {
        snackbarOpen(getApiMessage(response, 'Unable to update budget change request status.'), 'error');
        return;
      }

      setDetailCacheById((current) => {
        if (!current[modalState.rowId]) return current;

        const next = { ...current };
        delete next[modalState.rowId];
        return next;
      });
      setModalState(null);
      setRefreshNonce((current) => current + 1);
      snackbarOpen(getApiMessage(response, `${option.label} applied successfully.`), 'success');
    } finally {
      setIsStatusActionSubmitting(false);
    }
  }, [canApproveRequest, modalState, snackbarOpen]);

  const handleFilteredApiResponse = useCallback((response) => {
    const resetProjectContext = {
      projectId: activeProject.projectId || toProjectId(projectId),
      projectName: activeProject.projectName || sanitizeText(projectName, ''),
    };
    const normalizedResponse = normalizeTableUtilityResponse(response);
    const apiRows = normalizedResponse?.data?.tableData || [];
    const rawConfiguration = normalizedResponse?.data?.tableConfiguration || [];
    const isResetResponse = Boolean(response?.isReset || normalizedResponse?.isReset);
    const projectNameFilter = getFilterItem(rawConfiguration, 'projectName');
    if (Array.isArray(projectNameFilter?.options)) {
      projectNameFilterOptionsRef.current = projectNameFilter.options;
    }

    const responseProject = normalizedResponse?.data?.project
      || normalizedResponse?.data?.data?.project
      || {};
    const responseProjectContext = getProjectContextFromRawProject(responseProject);
    const appliedProjectSelection = getNormalizedFilterOption(
      additionalPayloadDataRef.current?.projectName?.selectedValue
      ?? additionalPayloadDataRef.current?.projectName?.selectedValues?.[0]
      ?? null,
    );
    const responseSelectedProjectOption = getSingleSelectFilterValue(projectNameFilter);
    const selectedProjectOption = isResetResponse
      ? getSingleSelectFilterValue(projectNameFilter, appliedProjectSelection)
      : (appliedProjectSelection || responseSelectedProjectOption);
    const selectedProjectNameLabel = getProjectSelectionName(selectedProjectOption);
    const hasProjectNameSelection = Boolean(selectedProjectOption);
    const shouldSeedRouteDefaults = isResetResponse || !hasProjectNameSelection;

    if (shouldSeedRouteDefaults) {
      const defaultProjectName = getNormalizedFilterOption(
        resetProjectContext.projectName
        || responseProjectContext.projectName
        || projectName,
      );

      if (projectNameFilter) {
        projectNameFilter.selectedValue = defaultProjectName;
        projectNameFilter.selectedValues = defaultProjectName ? [defaultProjectName] : [];
      }
    } else if (projectNameFilter && selectedProjectOption) {
      projectNameFilter.selectedValue = selectedProjectOption;
      projectNameFilter.selectedValues = [selectedProjectOption];
    }

    const selectedProjectContext = getProjectContextFromSelection(
      selectedProjectOption,
      projectNameFilter?.options,
      isResetResponse
        ? (resetProjectContext.projectId || projectId)
        : (responseProjectContext.projectId || appliedProjectContextRef.current?.projectId || null),
      isResetResponse
        ? (resetProjectContext.projectName || projectName)
        : (
          responseProjectContext.projectName
          || appliedProjectContextRef.current?.projectName
          || selectedProjectNameLabel
        ),
      responseProject,
    );
    const rowResolvedProjectContext = getProjectContextFromRows(
      apiRows,
      selectedProjectNameLabel,
      isResetResponse
        ? (resetProjectContext.projectId || projectId)
        : (responseProjectContext.projectId || appliedProjectContextRef.current?.projectId || null),
      isResetResponse
        ? (resetProjectContext.projectName || projectName)
        : (
          responseProjectContext.projectName
          || appliedProjectContextRef.current?.projectName
          || selectedProjectNameLabel
        ),
    );
    const nextProjectContext = {
      projectId: selectedProjectContext.projectId
        || rowResolvedProjectContext.projectId
        || responseProjectContext.projectId,
      projectName: selectedProjectContext.projectName
        || rowResolvedProjectContext.projectName
        || responseProjectContext.projectName
        || selectedProjectNameLabel,
    };
    visibleProjectContextRef.current = {
      projectId: rowResolvedProjectContext.projectId
        || nextProjectContext.projectId
        || responseProjectContext.projectId
        || appliedProjectContextRef.current?.projectId
        || toProjectId(projectId),
      projectName: rowResolvedProjectContext.projectName
        || nextProjectContext.projectName
        || responseProjectContext.projectName
        || appliedProjectContextRef.current?.projectName
        || sanitizeText(projectName, ''),
    };

    if (isResetResponse) {
      setAppliedProjectContext(resetProjectContext);
      if (
        resetProjectContext.projectId
        && resetProjectContext.projectId !== toProjectId(projectId)
      ) {
        navigate(buildBudgetChangeRequestRoute(
          resetProjectContext.projectId,
          location.search,
          resetProjectContext.projectName,
        ), {
          replace: true,
        });
      }
    } else if (
      nextProjectContext.projectId
      && (
        nextProjectContext.projectId !== activeProject.projectId
        || nextProjectContext.projectName !== activeProject.projectName
      )
    ) {
      setAppliedProjectContext(nextProjectContext);
      if (nextProjectContext.projectId !== toProjectId(projectId)) {
        navigate(buildBudgetChangeRequestRoute(
          nextProjectContext.projectId,
          location.search,
          nextProjectContext.projectName,
        ), {
          replace: true,
        });
      }
    }

    const rawRows = apiRows;
    const paginationData = normalizedResponse?.data?.pagination
      || normalizedResponse?.data?.paginationConfig
      || normalizedResponse?.data?.meta
      || normalizedResponse?.meta
      || {};
    const nextPage = Number(paginationData?.page) > 0 ? Number(paginationData.page) : 1;
    const nextLimit = Number(paginationData?.limit) > 0 ? Number(paginationData.limit) : 10;
    const nextTotal = Number(paginationData?.total) >= 0
      ? Number(paginationData.total)
      : rawRows.length;

    exportRowsRef.current = rawRows.map((row) => mapBcrRowForExport(row));
    exportProjectNameRef.current = resolveBcrProjectName(
      visibleProjectContextRef.current?.projectName
      || nextProjectContext.projectName
      || activeProject.projectName
      || projectName,
      {
        data: { tableData: rawRows },
        response: { data: normalizedResponse?.data },
      },
    ) || visibleProjectContextRef.current?.projectName || nextProjectContext.projectName || activeProject.projectName || projectName;
    const nextVisibleRowIds = rawRows
      .map((row) => row?.id)
      .filter((rowId) => rowId !== null && rowId !== undefined);
    const nextVisibleSelectableRowIds = rawRows
      .filter((row) => canApproveRequest && isSelectableStatus(getEffectiveStatusLabel(row)))
      .map((row) => row?.id)
      .filter((rowId) => rowId !== null && rowId !== undefined);
    const nextTableConfiguration = buildTableConfiguration({
      apiTableConfiguration: rawConfiguration,
      selectionHeader: (
        <SelectionHeaderCell
          onToggleAll={(checked) => {
            setSelectedRows((current) => {
              if (!checked) {
                return current.filter((rowId) => !nextVisibleSelectableRowIds.includes(rowId));
              }

              const nextSet = new Set([...current, ...nextVisibleSelectableRowIds]);
              return [...nextSet];
            });
          }}
        />
      ),
      showActionColumn: canEditRequest || canDeleteRequest,
      canViewEffort,
      canViewCost,
    });

    setVisibleSelectableRowIds(nextVisibleSelectableRowIds);
    setSelectedRows((current) => current.filter((rowId) => nextVisibleRowIds.includes(rowId)));
    let nextAdditionalPayload = getAppliedAdditionalPayload(
      rawConfiguration,
      isResetResponse ? buildRouteDefaultPayload() : additionalPayloadDataRef.current,
    );
    nextAdditionalPayload = {
      ...nextAdditionalPayload,
      ...(isResetResponse
        ? { project_id: resetProjectContext.projectId }
        : nextProjectContext.projectId
          ? { project_id: nextProjectContext.projectId }
          : {}),
    };
    if (!isResetResponse && selectedProjectOption) {
      nextAdditionalPayload.projectName = {
        ...(nextAdditionalPayload?.projectName || {}),
        selectedValue: selectedProjectOption,
        selectedValues: [selectedProjectOption],
      };
      nextAdditionalPayload.project_name = getProjectSelectionName(selectedProjectOption);
    }
    if (
      !isResetResponse
      && !nextProjectContext.projectId
      && getProjectSelectionName(nextAdditionalPayload?.projectName)
      && !isSameProjectSelectionName(nextAdditionalPayload?.projectName, resetProjectContext.projectName)
    ) {
      delete nextAdditionalPayload.project_id;
    }

    if (!arePayloadsEqual(additionalPayloadDataRef.current, nextAdditionalPayload)) {
      syncAdditionalPayloadDataRef(nextAdditionalPayload);
    }

    setPagination((current) => (
      current.page === nextPage && current.limit === nextLimit && current.total === nextTotal
        ? current
        : {
          page: nextPage,
          limit: nextLimit,
          total: nextTotal,
        }
    ));

    normalizedResponse.data.tableConfiguration = nextTableConfiguration;
    normalizedResponse.data.tableData = buildEnrichedTableData({
      tableData: rawRows,
      onToggleRow: handleToggleRow,
      onStatusActionSelect: handleRowStatusActionSelect,
      onEditRow: (row) => openModalWithDetail('edit', row),
      onDeleteRow: handleDeleteRow,
      canApprove: canApproveRequest,
      canEditRequest,
      canDeleteRequest,
    });
    normalizedResponse.data.paginationConfig = {
      page: nextPage,
      limit: nextLimit,
      total: nextTotal,
    };
    normalizedResponse.meta = {
      ...(normalizedResponse.meta || {}),
      page: nextPage,
      limit: nextLimit,
      total: nextTotal,
    };

    return normalizedResponse;
  }, [
    canApproveRequest,
    canDeleteRequest,
    canEditRequest,
    canViewCost,
    canViewEffort,
    buildRouteDefaultPayload,
    handleDeleteRow,
    handleToggleRow,
    handleRowStatusActionSelect,
    setAppliedProjectContext,
    openModalWithDetail,
    activeProject.projectId,
    activeProject.projectName,
    navigate,
    projectId,
    projectName,
    syncAdditionalPayloadDataRef,
  ]);

  const handlePaginationChange = useCallback((nextPagination) => {
    setPagination((current) => ({
      ...current,
      ...nextPagination,
    }));
  }, []);

  const handleModalSubmit = useCallback(async (payload, isDraft) => {
    if (modalState?.mode === 'add' && !canAddRequest) {
      snackbarOpen('You do not have access to create budget change requests.', 'error');
      return;
    }

    if (modalState?.mode === 'edit' && !canEditRequest) {
      snackbarOpen('You do not have access to update budget change requests.', 'error');
      return;
    }

    const requestPayload = mapModalPayloadToRequest({
      payload,
      isDraft,
      projectId: getCurrentActionProjectId(),
    });

    if (modalState?.mode === 'add') {
      setIsPendingRequestStatusLoading(true);
      const nextHasPendingRequest = await fetchPendingRequestStateForProject(requestPayload.project_id);
      setHasPendingRequest(nextHasPendingRequest);
      setIsPendingRequestStatusLoading(false);

      if (nextHasPendingRequest) {
        snackbarOpen('Only one Draft or Open request is allowed for a project.', 'error');
        return;
      }
    }

    setIsModalSubmitting(true);

    const response = modalState?.mode === 'edit' && modalState?.rowId
      ? await UseAxios(updateBudgetChangeRequest(modalState.rowId, requestPayload))
      : await UseAxios(createBudgetChangeRequest(requestPayload));

    setIsModalSubmitting(false);

    if (![200, 201].includes(response?.status)) {
      snackbarOpen(getApiMessage(response, 'Unable to save budget change request.'), 'error');
      return;
    }

    snackbarOpen(
      getApiMessage(
        response,
        modalState?.mode === 'edit'
          ? 'Budget change request updated.'
          : 'Budget change request created.',
      ),
      'success',
    );

    const savedDetailPayload = response?.data?.data || response?.data?.budgetChangeRequest || {};
    const normalizedSavedDetail = normalizeDetailPayload(
      savedDetailPayload,
      payload?.reasonForVersionChange || '',
    );
    const savedRowId = modalState?.rowId
      || normalizedSavedDetail?.header?.id
      || savedDetailPayload?.header?.id;

    if (savedRowId) {
      setDetailCacheById((current) => ({
        ...current,
        [savedRowId]: normalizedSavedDetail,
      }));
    }

    syncAdditionalPayloadDataRef(buildRouteDefaultPayload());
    setPagination((current) => ({
      ...current,
      page: 1,
    }));
    setModalState(null);
    setRefreshNonce((current) => current + 1);
  }, [
    canAddRequest,
    canEditRequest,
    modalState?.mode,
    modalState?.requestStatus,
    modalState?.rowId,
    buildRouteDefaultPayload,
    fetchPendingRequestStateForProject,
    getCurrentActionProjectId,
    snackbarOpen,
    syncAdditionalPayloadDataRef,
  ]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);

    try {
      const exportProjectId = visibleProjectContextRef.current?.projectId
        || appliedProjectContextRef.current?.projectId
        || activeProject.projectId;
      const buildExportRequest = (page, limit) => listBudgetChangeRequests({
        project_id: Number(exportProjectId),
        page,
        limit,
        filters: [],
        selectedViewId: null,
        searchQuery: '',
        userInfo: {
          ...(resolvedProfile || {}),
          globalColors: resolvedProfile?.siteConfiguration?.global_colors,
        },
      });
      const firstResponse = await UseAxios(buildExportRequest(1, 10000));

      if (firstResponse?.status !== 200) {
        snackbarOpen(getApiMessage(firstResponse, 'Unable to export budget change request.'), 'error');
        return;
      }

      let rawRows = getBcrRowsFromResponse(firstResponse);
      const rawConfiguration = getBcrConfigFromResponse(firstResponse);
      const paginationData = getBcrPaginationFromResponse(firstResponse);
      const totalRows = Number(paginationData?.total) || rawRows.length;
      const responseLimit = Number(paginationData?.limit) || rawRows.length || 10000;
      let nextPage = Number(paginationData?.page) || 1;

      while (rawRows.length < totalRows) {
        nextPage += 1;
        const nextResponse = await UseAxios(buildExportRequest(nextPage, responseLimit));

        if (nextResponse?.status !== 200) break;

        const nextRows = getBcrRowsFromResponse(nextResponse);
        if (!Array.isArray(nextRows) || nextRows.length === 0) break;

        rawRows = [...rawRows, ...nextRows];
      }

      if (!Array.isArray(rawRows) || rawRows.length === 0) {
        snackbarOpen('No data available to export.', 'error');
        return;
      }

      const resolvedExportProjectName = resolveBcrProjectName(
        activeProject.projectName || projectName,
        {
          data: { tableData: rawRows },
          response: { data: firstResponse?.data },
        },
      );
      const exportColumns = getBcrExportColumnsFromConfig({
        apiTableConfiguration: rawConfiguration,
        canViewEffort,
        canViewCost,
      });
      const exportRows = rawRows.map((row) => mapBcrRowForExport(row, exportColumns));
      const exportName = buildBudgetChangeRequestExportName(resolvedExportProjectName);
      const exportSheetName = buildBudgetChangeRequestSheetName(resolvedExportProjectName);

      await exportToExcel({
        data: exportRows,
        workBookTitle: exportName,
        workSheetTitle: exportSheetName,
        columnsConfig: exportColumns,
        prependRows: [
          {
            values: [exportName],
            font: { bold: true, size: 12 },
            alignment: { horizontal: 'left', vertical: 'middle' },
            mergeAcross: exportColumns.length,
          },
          { values: [''] },
        ],
        borderAllCells: true,
        outerBorder: true,
      });
      snackbarOpen('Budget change request exported successfully.', 'success');
    } catch (error) {
      snackbarOpen('Unable to export budget change request.', 'error');
    } finally {
      setIsExporting(false);
    }
  }, [canViewCost, canViewEffort, activeProject.projectId, activeProject.projectName, projectName, resolvedProfile, snackbarOpen]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      userInfo: {
        ...(resolvedProfile || {}),
        globalColors: resolvedProfile?.siteConfiguration?.global_colors,
      },
      apiEndpoints: {
        getFilteredData: { endpoint: 'list', enabled: true },
        download: { endpoint: '', enabled: false },
      },
      additionalPayloadData: additionalPayloadDataRef.current,
      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse,
      },
      headers: {
        token: accessToken,
      },
      showToolBar: true,
      showPagination: true,
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showExpandCollapse: false,
        showFilterOption: true,
        showToolBarHeading: true,
        toolBarHeading: 'Budget change request',
        showReset: false,
        additionalElements: buildToolbarElements({
          onOpenCostAnalysis: () => setIsCostAnalysisOpen(true),
          canViewCostAnalysis: canViewCost,
          canAddRequest,
          disableAddRequest: isPendingRequestStatusLoading,
          onAddRequest: handleAddNew,
          selectedCount: selectedRows.length,
          onOpenStatusUpdate: handleOpenBulkStatusUpdate,
          canApproveRequest,
          onExport: handleExport,
          isExporting,
        }),
      },
      handlers: {
        handleAddIconClick: handleAddNew,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: 10,
      paginationConfig: {
        ...pagination,
        onPaginationChange: handlePaginationChange,
        rowsPerPageOptions: [10, 20, 30, 40],
      },
      loaderSkeleton: {
        rows: 10,
        bodyCells: 2,
        columns: 8,
      },
      showExpandCollapse: false,
      tableScrollHeight: 'calc(100vh - 255px)',
      maxFrozenColumns: {
        desktop: 2,
        tablet: 2,
        mobile: 1,
      },
    },
  }), [
    accessToken,
    handleAddNew,
    handleOpenBulkStatusUpdate,
    handleCellClick,
    handleExport,
    handleFilteredApiResponse,
    handlePaginationChange,
    canAddRequest,
    canApproveRequest,
    canViewCost,
    hasPendingRequest,
    isPendingRequestStatusLoading,
    isExporting,
    pagination,
    additionalPayloadData,
    activeProject.projectId,
    activeProject.projectName,
    resolvedProfile,
    selectedRows.length,
  ]);

  return (
    <>
      <BudgetChangeRequestSelectionContext.Provider value={{ selectedRows, visibleSelectableRowIds }}>
        <div className={`budget-change-request-table-utility${selectedRows.length > 0 ? ' is-bulk-selection-active' : ''}`}>
          <TableUtility key={tableKey} configuration={configuration} />
        </div>
      </BudgetChangeRequestSelectionContext.Provider>

      {modalState && (
        <BudgetChangeRequestModal
          mode={modalState.mode}
          eern={modalState.eern}
          initialData={modalState.initialData}
          onClose={() => setModalState(null)}
          onSubmit={handleModalSubmit}
          onStatusSubmit={handleModalStatusSubmit}
          permissions={{
            canViewEffort,
            canViewCost,
            canEditEffort,
            canEditCost,
            canApproveRequest,
          }}
          resourceSkillOptions={resourceSkillOptions}
          costCategoryOptions={costCategoryOptions}
          approvalOptions={BULK_ACTION_OPTIONS}
          requestStatus={modalState.requestStatus}
          isSubmitting={isModalSubmitting}
          isStatusSubmitting={isStatusActionSubmitting}
        />
      )}

      {bulkActionDialog && (
        <StatusUpdateModal
          selectedCount={bulkActionDialog?.targetIds?.length || 0}
          projectName={bulkActionDialog?.projectName || ''}
          requestNumber={bulkActionDialog?.requestNumber || ''}
          option={bulkActionDialog.option}
          onOptionChange={(option) => setBulkActionDialog((current) => ({
            ...current,
            option,
          }))}
          comment={bulkActionDialog.comment}
          onCommentChange={(comment) => setBulkActionDialog((current) => ({
            ...current,
            comment,
          }))}
          onClose={() => setBulkActionDialog(null)}
          onSave={handleBulkActionSave}
        />
      )}

      {deleteConfirmRow && (
        <DeleteConfirmModal
          title="Delete Request"
          message="Are you sure you want to delete this request?"
          onClose={() => setDeleteConfirmRow(null)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isCostAnalysisOpen && (
        <CostAnalysisReportModal
          onClose={() => setIsCostAnalysisOpen(false)}
          onSubmit={() => setIsCostAnalysisOpen(false)}
        />
      )}
    </>
  );
};

SelectionHeaderCell.propTypes = {
  onToggleAll: PropTypes.func.isRequired,
};

SelectionRowCell.propTypes = {
  rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  eern: PropTypes.string.isRequired,
  requestStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SelectionRowCell.defaultProps = {
  requestStatus: '',
  disabled: false,
};

ActionRowCell.propTypes = {
  rowData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    eern: PropTypes.string.isRequired,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

StatusActionCell.propTypes = {
  rowData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    requestStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    requestStatusColor: PropTypes.string,
  }).isRequired,
  onActionSelect: PropTypes.func.isRequired,
  canApprove: PropTypes.bool.isRequired,
};

CostAnalysisReportModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

BudgetChangeRequestTable.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  projectName: PropTypes.string,
};

BudgetChangeRequestTable.defaultProps = {
  projectName: '',
};

export default BudgetChangeRequestTable;
